import { useEffect, useState, useMemo, Fragment, useRef } from "react";
import { api, formatApiError, openSecureFile } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Building2, ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown, Plus, Pencil, Trash2, Link2,
  Star, StarOff, X, User, Home, Landmark, Search, FlaskConical, Users, Mail,
  FileText, Download, Send, History, Archive, RotateCcw, Upload, Paperclip, Database,
  ArrowRightLeft, Wrench, ShieldCheck, Car, Package, TreePine, DoorOpen, Warehouse, Store, MoreHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { highlightMatch } from "../lib/highlightMatch";

// Iter122gf · Icona/tono coordinati al tipo unità (Appartamento/Box/Cantina/…)
const _TIPO_ICONS = {
  appartamento: { Icon: Home, tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  monolocale:   { Icon: Home, tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  attico:       { Icon: Home, tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  villa:        { Icon: Home, tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  box:          { Icon: Car, tone: "bg-amber-50 text-amber-800 border-amber-200" },
  autorimessa:  { Icon: Car, tone: "bg-amber-50 text-amber-800 border-amber-200" },
  garage:       { Icon: Car, tone: "bg-amber-50 text-amber-800 border-amber-200" },
  "posto auto": { Icon: Car, tone: "bg-amber-50 text-amber-800 border-amber-200" },
  cantina:      { Icon: Package, tone: "bg-stone-50 text-stone-800 border-stone-200" },
  soffitta:     { Icon: Package, tone: "bg-stone-50 text-stone-800 border-stone-200" },
  ripostiglio:  { Icon: Package, tone: "bg-stone-50 text-stone-800 border-stone-200" },
  magazzino:    { Icon: Warehouse, tone: "bg-slate-100 text-slate-800 border-slate-300" },
  laboratorio:  { Icon: Warehouse, tone: "bg-slate-100 text-slate-800 border-slate-300" },
  ufficio:      { Icon: Landmark, tone: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  studio:       { Icon: Landmark, tone: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  negozio:      { Icon: Store, tone: "bg-pink-50 text-pink-800 border-pink-200" },
  bar:          { Icon: Store, tone: "bg-pink-50 text-pink-800 border-pink-200" },
  giardino:     { Icon: TreePine, tone: "bg-green-50 text-green-800 border-green-200" },
  terrazzo:     { Icon: TreePine, tone: "bg-green-50 text-green-800 border-green-200" },
  androne:      { Icon: DoorOpen, tone: "bg-slate-50 text-slate-700 border-slate-200" },
  vano:         { Icon: DoorOpen, tone: "bg-slate-50 text-slate-700 border-slate-200" },
};
function TipoBadge({ tipo }) {
  const t = (tipo || "").trim();
  if (!t) return <span className="text-slate-400">—</span>;
  const key = t.toLowerCase();
  let cfg = _TIPO_ICONS[key];
  if (!cfg) {
    const found = Object.keys(_TIPO_ICONS).find((k) => key.startsWith(k) || key.includes(k));
    cfg = found ? _TIPO_ICONS[found] : { Icon: MoreHorizontal, tone: "bg-slate-50 text-slate-700 border-slate-200" };
  }
  const { Icon, tone } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${tone} text-xs font-semibold whitespace-nowrap`}>
      <Icon className="w-3.5 h-3.5" />
      <span>{t}</span>
    </span>
  );
}

const RUOLI_PROPR = ["Proprietario", "Comproprietario"];
const RUOLI_ALTRI_DIRITTI = ["Usufruttuario", "Nudo proprietario"];
const RUOLI_CONDUTT = ["Inquilino/Conduttore"];
const RUOLI_ALL = [...RUOLI_PROPR, ...RUOLI_ALTRI_DIRITTI, ...RUOLI_CONDUTT];

// Iter121c · Fase 3 · dropdown/options RAC coerenti con /rac/{token} pubblico
const DIRITTI_REALI = ["Piena proprietà", "Comproprietà", "Nuda proprietà", "Usufrutto",
                       "Diritto d'uso", "Diritto di abitazione"];
const TIPI_CASA = ["Prima casa", "Seconda casa"];
const POSSESSO_OPZIONI = [
  { value: "Prima casa - detraz. 50%", detr: "50" },
  { value: "Seconda casa - detraz. 36%", detr: "36" },
];

const ruoloTone = (r) => {
  if (RUOLI_PROPR.includes(r)) return "bg-emerald-100 text-emerald-800 border-emerald-300";
  if (RUOLI_CONDUTT.includes(r)) return "bg-sky-100 text-sky-800 border-sky-300";
  return "bg-amber-100 text-amber-800 border-amber-300";
};

export default function ProprietaUnitFirst({ condomini, filtroInitial }) {
  const [condMigrati, setCondMigrati] = useState([]);
  const [cond, setCond] = useState(filtroInitial || "");
  const [unita, setUnita] = useState([]);
  const [soggetti, setSoggetti] = useState([]);
  const [storico, setStorico] = useState([]);
  const [cestino, setCestino] = useState({ unita: [], soggetti: [] });
  const [tab, setTab] = useState("unita"); // "unita" | "soggetti" | "storico" | "cestino"
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const [modal, setModal] = useState(null); // { type, data }
  const [selectedSoggetti, setSelectedSoggetti] = useState(new Set());
  const [selectedUnita, setSelectedUnita] = useState(new Set());
  const [racModal, setRacModal] = useState(null); // {condominio_id, condominio_nome}
  const [migraRacModal, setMigraRacModal] = useState(null); // Iter121g · {cond_id, step: 'confirm'|'preview'|'done', report?}
  const [racFilter, setRacFilter] = useState("all"); // Iter121h · "all" | "completi" | "incompleti"
  const [invioAnagrafiche, setInvioAnagrafiche] = useState(false); // Iter121l · toggle globale invio RAC
  const [stats, setStats] = useState(null); // Iter121d · Fase 4 · KPI RAC letti da Unit-First
  const [busy, setBusy] = useState(false);
  // Iter122jt · Toggle "Riparazioni avanzate": nasconde i 4 pulsanti singoli (sub multipli,
  // contatti, tipo unità, duplicati soggetti) dietro un menu collassabile. Il pulsante
  // principale "Diagnostica e ripara" resta la strada consigliata.
  const [showAdvancedRepairs, setShowAdvancedRepairs] = useState(false);

  const [aggregatiInfo, setAggregatiInfo] = useState({ ids: new Set(), byUnita: {} });

  const loadCondomini = () =>
    api.get("/refactor/condomini-migrati").then((r) => setCondMigrati(r.data.condomini || [])).catch(() => {});

  const loadData = () => {
    if (!cond) { setUnita([]); setSoggetti([]); setStorico([]); setCestino({ unita: [], soggetti: [] }); setAggregatiInfo({ ids: new Set(), byUnita: {} }); return; }
    api.get("/refactor/unita", { params: { condominio_id: cond } })
      .then((r) => setUnita(r.data.unita || [])).catch(() => setUnita([]));
    api.get("/refactor/soggetti", { params: { condominio_id: cond } })
      .then((r) => setSoggetti(r.data.soggetti || [])).catch(() => setSoggetti([]));
    api.get("/refactor/storico", { params: { condominio_id: cond } })
      .then((r) => setStorico(r.data.storico || [])).catch(() => setStorico([]));
    api.get("/refactor/cestino", { params: { condominio_id: cond } })
      .then((r) => setCestino(r.data || { unita: [], soggetti: [] }))
      .catch(() => setCestino({ unita: [], soggetti: [] }));
    // Iter121d · KPI RAC dal nuovo modello (source-of-truth Unit-First)
    api.get("/refactor/stato-anagrafica", { params: { condominio_id: cond } })
      .then((r) => setStats(r.data)).catch(() => setStats(null));
    // Iter121l · stato del toggle invio richieste anagrafiche (RAC)
    api.get("/settings").then((r) => setInvioAnagrafiche(!!r.data.invio_anagrafiche)).catch(() => {});
    // Iter122fw · Aggregati candidati per badge in tabella + banner
    api.get(`/condomini/${cond}/aggregati-candidati`).then((r) => {
      const ids = new Set();
      const byU = {};
      (r.data || []).forEach((a) => {
        ids.add(a.aggregato_id);
        if (!byU[a.unita_id]) byU[a.unita_id] = [];
        byU[a.unita_id].push(a);
      });
      setAggregatiInfo({ ids, byUnita: byU });
    }).catch(() => setAggregatiInfo({ ids: new Set(), byUnita: {} }));
    setSelectedSoggetti(new Set());
    setSelectedUnita(new Set());
  };

  useEffect(() => { loadCondomini(); }, []);
  useEffect(() => { loadData(); }, [cond]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first migrated condominio if none
  useEffect(() => {
    if (condMigrati.length > 0 && !cond) setCond(condMigrati[0].id);
  }, [condMigrati]);

  const norm = (s) => (s || "").toString().toLowerCase().trim();
  const filteredUnita = useMemo(() => {
    let base = unita;
    if (query.trim()) {
      const q = norm(query);
      base = base.filter((u) => {
        if ([u.interno, u.sub, u.foglio, u.mappale_particella, u.palazzina, u.scala, u.piano].some((v) => norm(v).includes(q))) return true;
        return (u.soggetti_collegati || []).some((s) => norm(s.nome).includes(q) || norm(s.cf).includes(q));
      });
    }
    // Iter121h · filtro per stato RAC (dai KPI badge cliccabili)
    if (racFilter !== "all") {
      base = base.filter((u) => {
        const rels = u.soggetti_collegati || [];
        if (rels.length === 0) return racFilter === "incompleti"; // unità senza soggetti = "da completare"
        const allOk = rels.every((s) => {
          if (s.ruolo === "Inquilino/Conduttore") return !!(s.inizio_locazione && s.fine_locazione && s.num_occupanti);
          return !!(s.quota && s.diritto_reale && s.tipo_casa && s.percentuale_detrazione && s.num_occupanti);
        });
        return racFilter === "completi" ? allOk : !allOk;
      });
    }
    return base;
  }, [unita, query, racFilter]);
  const filteredSoggetti = useMemo(() => {
    if (!query.trim()) return soggetti;
    const q = norm(query);
    return soggetti.filter((s) => [s.nome, s.codice_fiscale, s.email, s.telefono].some((v) => norm(v).includes(q)));
  }, [soggetti, query]);

  const toggleExpand = (id) => setExpanded((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const eliminaUnita = async (u) => {
    if (!window.confirm(`Eliminare l'unità "${u.interno || u.sub}"?\n\nSe ha soggetti collegati attivi, verrà richiesta conferma per forzare l'eliminazione.`)) return;
    try {
      await api.delete(`/refactor/unita/${u.id}`);
      toast.success("Unità spostata nel cestino");
      loadData();
    } catch (e) {
      // Iter119 · se blocco per soggetti attivi (409), offri il bypass "force"
      if (e.response?.status === 409) {
        const detail = e.response?.data?.detail || "";
        if (window.confirm(`${detail}\n\nVuoi FORZARE l'eliminazione? Tutte le relazioni attive (incluso il referente account) verranno chiuse e l'unità andrà nel cestino.`)) {
          try {
            const r = await api.delete(`/refactor/unita/${u.id}`, { params: { force: true } });
            toast.success(`Unità eliminata (${r.data.relazioni_chiuse || 0} relazioni chiuse)`);
            loadData();
          } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
        }
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Eliminazione non riuscita");
      }
    }
  };
  const chiudiRelazione = async (rid) => {
    const motivo = window.prompt("Motivo chiusura (opzionale, es. Vendita, Cambio inquilino):", "Chiusura manuale");
    if (motivo === null) return;
    try {
      await api.delete(`/refactor/soggetti-unita/${rid}`, { params: motivo ? { motivo } : {} });
      toast.success("Relazione chiusa");
      loadData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setReferente = async (rid) => {
    try { await api.post(`/refactor/soggetti-unita/${rid}/set-referente`); toast.success("Referente aggiornato"); loadData(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const richiediRAC = async (s) => {
    if (!s.email) { toast.error(`${s.nome} non ha email registrata. Aggiungila prima.`); return; }
    if (!window.confirm(`Inviare la richiesta RAC (aggiornamento dati catastali) a:\n${s.nome} <${s.email}>?\n\nIl condòmino riceverà un'email con il link al modulo pubblico.`)) return;
    try {
      const { data } = await api.post(`/refactor/soggetti/${s.id}/richiedi-rac`);
      toast.success(`RAC inviata a ${data.email}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito"); }
  };

  // ============== FASE 4b — Bulk actions ==============
  const toggleSelectSoggetto = (sid) => setSelectedSoggetti((prev) => {
    const n = new Set(prev);
    if (n.has(sid)) n.delete(sid); else n.add(sid);
    return n;
  });
  const toggleSelectAllSoggetti = () => {
    if (selectedSoggetti.size === filteredSoggetti.length) setSelectedSoggetti(new Set());
    else setSelectedSoggetti(new Set(filteredSoggetti.map((s) => s.id)));
  };

  // Iter120c · selezione multipla unità per bulk-delete
  const toggleSelectUnita = (uid) => setSelectedUnita((prev) => {
    const n = new Set(prev);
    if (n.has(uid)) n.delete(uid); else n.add(uid);
    return n;
  });
  const toggleSelectAllUnita = () => {
    if (selectedUnita.size === filteredUnita.length) setSelectedUnita(new Set());
    else setSelectedUnita(new Set(filteredUnita.map((u) => u.id)));
  };
  const eliminaUnitaSelezionate = async () => {
    if (selectedUnita.size === 0) { toast.error("Seleziona almeno un'unità"); return; }
    if (!window.confirm(
      `Eliminare ${selectedUnita.size} unità selezionate?\n\n` +
      `Tutte le eventuali relazioni attive (inclusi i referenti account) verranno chiuse ` +
      `e le unità verranno spostate nel Cestino. L'operazione è ripristinabile dal Cestino.`
    )) return;
    setBusy(true);
    let ok = 0, err = 0, relChiuse = 0;
    for (const uid of Array.from(selectedUnita)) {
      try {
        const r = await api.delete(`/refactor/unita/${uid}`, { params: { force: true } });
        ok += 1;
        relChiuse += (r.data?.relazioni_chiuse || 0);
      } catch { err += 1; }
    }
    if (err === 0) toast.success(`${ok} unità eliminate (${relChiuse} relazioni chiuse)`);
    else toast.error(`Eliminate ${ok} · errori ${err}`);
    setSelectedUnita(new Set());
    setBusy(false);
    loadData();
  };

  const sollecitaSelezionati = async () => {
    if (selectedSoggetti.size === 0) { toast.error("Seleziona almeno un soggetto"); return; }
    if (!window.confirm(`Inviare la richiesta RAC a ${selectedSoggetti.size} soggetti selezionati?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/refactor/rac/bulk", { soggetti_ids: Array.from(selectedSoggetti) });
      toast.success(`RAC inviate: ${data.inviati} · senza email: ${data.senza_email} · errori: ${data.errori}`);
      setSelectedSoggetti(new Set());
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio massivo non riuscito"); }
    finally { setBusy(false); }
  };

  const sollecitaCondominio = async () => {
    if (!window.confirm(`Inviare la richiesta RAC a TUTTI i soggetti del condominio? (${soggetti.length})`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/refactor/rac/bulk", { condominio_id: cond });
      toast.success(`RAC inviate: ${data.inviati} · senza email: ${data.senza_email} · errori: ${data.errori}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio massivo non riuscito"); }
    finally { setBusy(false); }
  };

  // Iter121f · Sollecita solo i soggetti che hanno relazioni RAC incomplete
  // (targeting intelligente basato sui dati Unit-First già caricati)
  const soggettiIncompleti = useMemo(() => {
    const idsIncompleti = new Set();
    for (const u of unita) {
      for (const s of (u.soggetti_collegati || [])) {
        const isCond = s.ruolo === "Inquilino/Conduttore";
        const racOk = isCond
          ? (s.inizio_locazione && s.fine_locazione && s.num_occupanti)
          : (s.quota && s.diritto_reale && s.tipo_casa && s.percentuale_detrazione && s.num_occupanti);
        if (!racOk) idsIncompleti.add(s.id);
      }
    }
    return Array.from(idsIncompleti);
  }, [unita]);

  const sollecitaIncompleti = async () => {
    if (soggettiIncompleti.length === 0) { toast.info("Tutti i soggetti hanno il RAC completo — nessun sollecito da inviare."); return; }
    if (!window.confirm(`Inviare la richiesta RAC solo ai ${soggettiIncompleti.length} soggetti con dati incompleti?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/refactor/rac/bulk", { soggetti_ids: soggettiIncompleti });
      toast.success(`RAC inviate: ${data.inviati} · senza email: ${data.senza_email} · errori: ${data.errori}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito"); }
    finally { setBusy(false); }
  };

  // Iter121g · Migrazione dati RAC dal legacy `proprieta` al nuovo `soggetti_unita`.
  // 2 step: (1) dry-run che mostra il report, (2) esecuzione reale su conferma.
  // Iter121l · Toggle globale "Invio richieste anagrafiche (RAC)"
  const toggleInvioAnagrafiche = async (val) => {
    setInvioAnagrafiche(val);
    try {
      await api.put("/settings", { invio_anagrafiche: val });
      toast.success(val
        ? "Invio RAC ATTIVATO: da ora i solleciti anagrafici partiranno."
        : "Invio RAC sospeso: nessuna richiesta partirà (utile durante configurazione).");
    } catch {
      setInvioAnagrafiche(!val);
      toast.error("Impossibile aggiornare l'impostazione");
    }
  };

  // Iter121h · Download PDF RAC per singola unità
  const downloadRacPdfUnita = async (uid, unitaLabel) => {
    setBusy(true);
    try {
      const r = await api.get(`/refactor/unita/${uid}/rac/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `RAC_UI_${(unitaLabel || uid).replace(/\s+/g, "_")}.pdf`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("PDF RAC scaricato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Nessun soggetto attivo su questa unità");
    } finally { setBusy(false); }
  };

  // Iter121i · Download PDF RAC per singola anagrafica (aggrega tutte le unità del soggetto)
  const downloadRacPdfSoggetto = async (sid, soggNome) => {
    setBusy(true);
    try {
      const r = await api.get(`/refactor/soggetti/${sid}/rac/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `RAC_${(soggNome || sid).replace(/\s+/g, "_")}.pdf`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("PDF RAC anagrafica scaricato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Nessuna unità attiva per questo soggetto");
    } finally { setBusy(false); }
  };

  // Iter121h · Helper coerente lato client con la logica backend (_rac_completa)
  const _relazioneRacOk = (s) => {
    if (s.ruolo === "Inquilino/Conduttore") {
      return !!(s.inizio_locazione && s.fine_locazione && s.num_occupanti);
    }
    return !!(s.quota && s.diritto_reale && s.tipo_casa && s.percentuale_detrazione && s.num_occupanti);
  };
  const _unitaRacStatus = (u) => {
    const rels = u.soggetti_collegati || [];
    if (rels.length === 0) return "nessuno"; // niente da compilare
    const complete = rels.filter(_relazioneRacOk).length;
    if (complete === 0) return "incompleto";
    if (complete === rels.length) return "completo";
    return "parziale";
  };

  const runMigraRac = async (dry_run) => {
    setBusy(true);
    try {
      const body = { dry_run };
      if (migraRacModal?.cond_id) body.condominio_id = migraRacModal.cond_id;
      const { data } = await api.post("/refactor/migra-dati-rac", body);
      setMigraRacModal({ ...migraRacModal, step: dry_run ? "preview" : "done", report: data });
      if (!dry_run) {
        toast.success(`Migrazione completata · ${data.relazioni_aggiornate} aggiornate · ${data.titolari_promossi_a_soggetto} titolari promossi`);
        loadData();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Migrazione non riuscita"); }
    finally { setBusy(false); }
  };

  const exportExcel = async (formato) => {
    setBusy(true);
    try {
      const r = await api.get("/refactor/export", { params: { condominio_id: cond, formato }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const cn = (condOpts.find((c) => c.id === cond) || {}).nome || "Tutti";
      a.download = `anagrafica_unit_first_${cn.replace(/\s+/g, "_")}_${formato}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Excel scaricato (${formato})`);
    } catch (e) { toast.error("Export non riuscito"); }
    finally { setBusy(false); }
  };

  const exportDanea = async () => {
    setBusy(true);
    try {
      const r = await api.get("/refactor/export-danea", { params: { condominio_id: cond }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const cn = (condOpts.find((c) => c.id === cond) || {}).nome || "Tutti";
      a.download = `export_danea_${cn.replace(/\s+/g, "_")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export Danea scaricato (compatibile re-import in Danea Domus Studio)");
    } catch (e) {
      const msg = e.response?.status === 404 ? "Nessuna anagrafica da esportare per questo condominio" : "Export non riuscito";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  // Iter122fp8 · Cleanup subalterni multi-valore residui (es. sub="722, 716")
  // lasciati da import Danea PDF/Excel pre-fix Iter122fp2. Flusso:
  //   1) dry-run → mostra conteggio previsto
  //   2) conferma utente → apply
  //   3) reload dati
  const riparaSubMultipli = async () => {
    if (!cond) { toast.error("Seleziona prima un condominio"); return; }
    setBusy(true);
    try {
      const { data: preview } = await api.post(`/refactor/cleanup-sub-multipli?condominio_id=${cond}&dry_run=true`);
      const st = preview.stats || {};
      if ((st.normalizzate || 0) === 0) {
        toast.success("Nessuna unità con subalterni multi-valore trovata: dati puliti ✓");
        return;
      }
      const msg = `Trovate ${st.normalizzate} unità con subalterni multi-valore (es. "722, 716").\n\n`
        + `Verranno:\n`
        + `• Normalizzate al PRIMO sub\n`
        + `• Create ${st.unita_create} nuove unità per i sub extra mancanti\n`
        + `• ${st.extra_sub_gia_presenti} sub extra già presenti (skip)\n`
        + `• Aggiunte ${st.relazioni_create} relazioni soggetto-unità\n\n`
        + `Prima di procedere, ti consiglio di scaricare l'Export Danea come backup.\n\n`
        + `Confermi la riparazione?`;
      if (!window.confirm(msg)) return;
      const { data: applied } = await api.post(`/refactor/cleanup-sub-multipli?condominio_id=${cond}&dry_run=false`);
      const sa = applied.stats || {};
      toast.success(`Cleanup completato: ${sa.normalizzate} unità normalizzate, ${sa.unita_create} create, ${sa.relazioni_create} relazioni aggiunte`);
      loadData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore cleanup sub multipli");
    } finally { setBusy(false); }
  };

  // Iter122fp10 · Cleanup duplicati soggetti (stesso CF + cognome).
  // Bug scoperto in produzione dopo re-import PDF: "Scotti Paola" moltiplicato per 3.
  const riparaDuplicatiSoggetti = async () => {
    if (!cond) { toast.error("Seleziona prima un condominio"); return; }
    setBusy(true);
    try {
      const { data: preview } = await api.post(`/refactor/cleanup-duplicati-soggetti?condominio_id=${cond}&dry_run=true`);
      const st = preview.stats || {};
      if ((st.duplicati_uniti || 0) === 0) {
        toast.success("Nessun soggetto duplicato trovato: dati puliti ✓");
        return;
      }
      const msg = `Trovati ${st.gruppi} gruppi di soggetti duplicati (stesso CF + cognome).\n\n`
        + `Verranno:\n`
        + `• Uniti ${st.duplicati_uniti} soggetti duplicati sul primario (quello con account/più relazioni)\n`
        + `• Spostate ${st.relazioni_spostate} relazioni sul primario\n`
        + `• Dedotte ${st.relazioni_dedupe} relazioni doppie (stesso soggetto+unità)\n`
        + `• I duplicati verranno marcati come cancellati (recuperabili dal cestino)\n\n`
        + `Confermi la riparazione?`;
      if (!window.confirm(msg)) return;
      const { data: applied } = await api.post(`/refactor/cleanup-duplicati-soggetti?condominio_id=${cond}&dry_run=false`);
      const sa = applied.stats || {};
      toast.success(`Cleanup completato: ${sa.duplicati_uniti} soggetti uniti, ${sa.relazioni_spostate} relazioni spostate, ${sa.relazioni_dedupe} deduplicate`);
      loadData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore cleanup duplicati soggetti");
    } finally { setBusy(false); }
  };

  // Iter122gf · Diagnostica e Ripara: esegue in sequenza tutte le manutenzioni
  //  1) sub multipli  2) duplicati soggetti  3) tipo unità  4) contatti account
  const diagnosticaERipara = async () => {
    setBusy(true);
    try {
      const scope = cond ? `?condominio_id=${cond}` : "";
      // Fase 1: preview (dry-run) parallela
      const [subP, dupP, tipoP, contP] = await Promise.all([
        api.post(`/refactor/cleanup-sub-multipli${scope}${scope ? "&" : "?"}dry_run=true`).then((r) => r.data).catch(() => null),
        api.post(`/refactor/cleanup-duplicati-soggetti${scope}${scope ? "&" : "?"}dry_run=true`).then((r) => r.data).catch(() => null),
        api.post(`/refactor/backfill-tipo-unita${scope}${scope ? "&" : "?"}dry_run=true`).then((r) => r.data).catch(() => null),
        api.post(`/soggetti/sistema-email-account${scope}${scope ? "&" : "?"}dry_run=true`).then((r) => r.data).catch(() => null),
      ]);
      const n_sub = subP?.da_aggiornare ?? subP?.corrections ?? 0;
      const n_dup = dupP?.da_aggiornare ?? dupP?.gruppi_duplicati?.length ?? 0;
      const n_tipo = tipoP?.da_aggiornare ?? 0;
      const n_em = contP?.email_aggiornate ?? 0;
      const n_tel = contP?.telefono_aggiornati ?? 0;
      const totale = n_sub + n_dup + n_tipo + n_em + n_tel;
      if (totale === 0) {
        toast.success("Tutto in ordine ✓ Nessuna correzione da applicare.");
        return;
      }
      const lines = [];
      if (n_sub) lines.push(`• ${n_sub} sub multipli da normalizzare`);
      if (n_dup) lines.push(`• ${n_dup} gruppi di soggetti duplicati da unire`);
      if (n_tipo) lines.push(`• ${n_tipo} unità senza tipo (Appartamento/Box/…)`);
      if (n_em || n_tel) lines.push(`• ${n_em} email + ${n_tel} telefoni disallineati dall'account`);
      const scopeLabel = cond ? "questo condominio" : "TUTTI i condomìni";
      if (!window.confirm(`Diagnostica ${scopeLabel}:\n\n${lines.join("\n")}\n\nApplicare le correzioni in blocco?`)) return;

      // Fase 2: applica in sequenza (per non stressare il DB e mostrare progressi coerenti)
      const results = [];
      if (n_sub) {
        const { data } = await api.post(`/refactor/cleanup-sub-multipli${scope}${scope ? "&" : "?"}dry_run=false`);
        results.push(`sub multipli: ${data.corrections ?? data.aggiornati ?? n_sub}`);
      }
      if (n_dup) {
        const { data } = await api.post(`/refactor/cleanup-duplicati-soggetti${scope}${scope ? "&" : "?"}dry_run=false`);
        results.push(`duplicati: ${data.merged ?? data.aggiornati ?? n_dup}`);
      }
      if (n_tipo) {
        const { data } = await api.post(`/refactor/backfill-tipo-unita${scope}${scope ? "&" : "?"}dry_run=false`);
        results.push(`tipo unità: ${data.aggiornati}`);
      }
      if (n_em || n_tel) {
        const { data } = await api.post(`/soggetti/sistema-email-account${scope}${scope ? "&" : "?"}dry_run=false`);
        results.push(`contatti: ${data.email_aggiornate}E + ${data.telefono_aggiornati}T`);
      }
      toast.success(`Diagnostica completata: ${results.join(" · ")}`);
      loadData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore Diagnostica e Ripara");
    } finally { setBusy(false); }
  };

  // Iter122ge · Backfill retroattivo del campo "Tipo unità" (Appartamento/Box/Cantina)
  const backfillTipoUnita = async () => {
    setBusy(true);
    try {
      const params = cond ? `?condominio_id=${cond}&dry_run=true` : "?dry_run=true";
      const { data: preview } = await api.post(`/refactor/backfill-tipo-unita${params}`);
      const n = preview.da_aggiornare || 0;
      if (n === 0) {
        toast.success("Nessuna unità con tipo mancante. Tutto a posto ✓");
        return;
      }
      const sample = (preview.dettaglio || []).slice(0, 5)
        .map((d) => `• ${d.interno || d.sub || "—"} → ${d.tipo_unita}`).join("\n");
      const restanti = n > 5 ? `\n\n…e altre ${n - 5} unità` : "";
      if (!window.confirm(`Trovate ${n} unità senza "Tipo unità" valorizzato.\n\nEsempi:\n${sample}${restanti}\n\nApplicare?`)) return;
      const applyParams = cond ? `?condominio_id=${cond}&dry_run=false` : "?dry_run=false";
      const { data: applied } = await api.post(`/refactor/backfill-tipo-unita${applyParams}`);
      toast.success(`${applied.aggiornati} unità aggiornate con il tipo (Appartamento/Box/…)`);
      loadData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore backfill tipo unità");
    } finally { setBusy(false); }
  };

  // Iter122gb + Iter122gc · Migrazione retroattiva: allinea email E telefono soggetti all'account
  const sistemaEmailAccount = async () => {
    setBusy(true);
    try {
      const params = cond ? `?condominio_id=${cond}&dry_run=true` : "?dry_run=true";
      const { data: preview } = await api.post(`/soggetti/sistema-email-account${params}`);
      const n = preview.da_aggiornare || 0;
      if (n === 0) {
        toast.success(`Anagrafiche già allineate agli account (${preview.scanned} scansionati) ✓`);
        return;
      }
      const preview_sample = (preview.dettaglio || []).slice(0, 5).map((d) => {
        const label = (d.cambi || []).map((c) => `${c.campo}: ${c.prima || "(vuoto)"} → ${c.dopo}`).join(" | ");
        return `• ${d.nome}: ${label}`;
      }).join("\n");
      const restanti = n > 5 ? `\n\n…e altri ${n - 5} soggetti` : "";
      const em = preview.email_aggiornate || 0;
      const tel = preview.telefono_aggiornati || 0;
      const parts = [em ? `${em} email` : null, tel ? `${tel} telefoni` : null].filter(Boolean).join(" + ");
      const msg = `Trovati ${n} soggetti con contatti disallineati dall'account (${parts}):\n\n${preview_sample}${restanti}\n\n`
        + `Applicare le correzioni? Email e telefono di ciascuna anagrafica verranno aggiornati ai valori dell'account collegato.`;
      if (!window.confirm(msg)) return;
      const applyParams = cond ? `?condominio_id=${cond}&dry_run=false` : "?dry_run=false";
      const { data: applied } = await api.post(`/soggetti/sistema-email-account${applyParams}`);
      const p2 = [
        applied.email_aggiornate ? `${applied.email_aggiornate} email` : null,
        applied.telefono_aggiornati ? `${applied.telefono_aggiornati} telefoni` : null,
      ].filter(Boolean).join(" + ");
      toast.success(`${applied.aggiornati} anagrafiche aggiornate (${p2})`);
      loadData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore sistemazione contatti");
    } finally { setBusy(false); }
  };

  // Iter122gd · Elimina definitivamente una relazione chiusa (storico)
  const purgeRelazione = async (r) => {
    const dataStr = r.al ? new Date(r.al).toLocaleDateString("it-IT") : "?";
    const nome = r.soggetto_nome || r.nome || "—";
    if (!window.confirm(
      `Eliminare definitivamente questa relazione dallo storico?\n\n` +
      `Soggetto: ${nome}\nRuolo: ${r.ruolo || "—"}\nChiusa il: ${dataStr}\nMotivo: ${r.motivo || "—"}\n\n` +
      `Questa operazione non può essere annullata.`
    )) return;
    try {
      await api.delete(`/refactor/soggetti-unita/${r.relazione_id}/purge`);
      toast.success("Relazione storica eliminata");
      loadData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Eliminazione non riuscita");
    }
  };

  // Iter122fp10 · Sostituzione inline aggregato → individuo (dal banner unità).
  const sostituisciAggregato = async (aggr, target) => {
    const msg = `Sostituire "${aggr.aggregato_nome}" con "${target.nome}"?\n\n`
      + `• L'account, i consensi privacy e i documenti dell'aggregato verranno spostati su "${target.nome}"\n`
      + `• L'aggregato verrà archiviato (soft delete, recuperabile)\n\n`
      + `Confermi?`;
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/soggetti/${aggr.aggregato_id}/sostituisci`, {
        target_soggetto_id: target.soggetto_id,
        unita_id: aggr.unita_id,
        migra_account: true,
        motivo: "Sostituzione aggregato da banner Anagrafica",
      });
      toast.success(`"${aggr.aggregato_nome}" sostituito con "${target.nome}" ✓`);
      loadData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore sostituzione aggregato");
    }
  };

  const ripristina = async (tipo, item_id) => {
    if (!window.confirm(`Ripristinare questo ${tipo} dal cestino?`)) return;
    try {
      await api.post(`/refactor/cestino/${tipo}/${item_id}/ripristina`);
      toast.success("Ripristinato");
      loadData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const eliminaDefinitivo = async (tipo, item_id) => {
    if (!window.confirm(`⚠️ ELIMINARE DEFINITIVAMENTE questo ${tipo}? Operazione IRREVERSIBILE.`)) return;
    try {
      await api.delete(`/refactor/cestino/${tipo}/${item_id}`);
      toast.success("Eliminato definitivamente");
      loadData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const eliminaSoggetto = async (s) => {
    if (!window.confirm(`Spostare "${s.nome}" nel cestino?\n\nIl soggetto NON deve avere relazioni attive.`)) return;
    try {
      await api.delete(`/refactor/soggetti/${s.id}`);
      toast.success("Soggetto spostato nel cestino");
      loadData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const condOpts = condomini || [];
  const isMigrated = (cid) => condMigrati.some((c) => c.id === cid);

  return (
    <div data-testid="proprieta-unit-first">
      {/* Iter122ez · Toggle invio RAC integrato nella card KPI sottostante per ridurre lo spazio.
          Quando non ci sono stats (nessuna relazione), mostriamo comunque una barra compatta a una riga. */}
      {(!stats || stats.totale === 0) && (
        <Card className={`p-2 mb-4 border-2 ${invioAnagrafiche ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`} data-testid="invio-rac-toggle-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className={`text-sm font-semibold flex items-center gap-1.5 ${invioAnagrafiche ? "text-emerald-900" : "text-amber-900"}`}>
              <Send className="w-4 h-4" />
              Invio RAC: {invioAnagrafiche ? "ATTIVO ✓" : "DISATTIVO ⚠"}
              <span className="hidden sm:inline text-xs font-normal text-slate-600 ml-2">
                {invioAnagrafiche ? "· email in partenza" : "· email bloccate finché non attivi"}
              </span>
            </div>
            <label className="inline-flex items-center cursor-pointer" data-testid="invio-rac-toggle-label">
              <input type="checkbox" className="sr-only peer" checked={invioAnagrafiche}
                onChange={(e) => toggleInvioAnagrafiche(e.target.checked)}
                data-testid="invio-rac-toggle-input" />
              <div className="relative w-10 h-5 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-600" />
            </label>
          </div>
        </Card>
      )}

      {/* Iter121d · Fase 4 · KPI RAC — letti dal nuovo modello Unit-First */}
      {stats && stats.totale > 0 && (
        <Card className={`p-3 mb-4 border-2 ${invioAnagrafiche ? "" : "border-amber-300"}`} data-testid="rac-kpi-banner">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-brand" />
              Stato compilazione RAC {cond ? "in questo condominio" : "globale"}:
            </div>
            <div className="flex items-center gap-2 text-sm flex-wrap">
              {/* Iter121h · Badge cliccabili → filtrano la tabella unità */}
              <button data-testid="rac-kpi-completi"
                onClick={() => { setRacFilter(racFilter === "completi" ? "all" : "completi"); setTab("unita"); }}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg font-bold transition-all hover:scale-105 ${racFilter === "completi" ? "bg-emerald-600 text-white ring-2 ring-emerald-300" : "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"}`}
                title="Clicca per filtrare le unità con RAC completo">
                <span className={`w-2 h-2 rounded-full ${racFilter === "completi" ? "bg-white" : "bg-emerald-500"}`} /> {stats.completi} completi
              </button>
              <button data-testid="rac-kpi-incompleti"
                onClick={() => { setRacFilter(racFilter === "incompleti" ? "all" : "incompleti"); setTab("unita"); }}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg font-bold transition-all hover:scale-105 ${racFilter === "incompleti" ? "bg-amber-600 text-white ring-2 ring-amber-300" : "bg-amber-100 text-amber-800 hover:bg-amber-200"}`}
                title="Clicca per filtrare le unità con dati RAC mancanti">
                <span className={`w-2 h-2 rounded-full ${racFilter === "incompleti" ? "bg-white" : "bg-amber-500"}`} /> {stats.da_completare} da completare
              </button>
              <span className="text-slate-500">su {stats.totale} relazioni</span>
              <span data-testid="rac-kpi-perc" className="text-slate-700 font-bold">
                {stats.totale > 0 ? Math.round(stats.completi / stats.totale * 100) : 0}%
              </span>
              {racFilter !== "all" && (
                <button data-testid="rac-kpi-clear-filter"
                  onClick={() => setRacFilter("all")}
                  className="text-xs underline text-slate-500 hover:text-slate-800 ml-1">
                  ✕ rimuovi filtro
                </button>
              )}
              {/* Iter121f · Sollecita solo gli incompleti (targeting intelligente) */}
              {cond && soggettiIncompleti.length > 0 && (
                <Button data-testid="rac-kpi-sollecita-incompleti"
                  size="sm" className="h-8 bg-amber-600 hover:bg-amber-700 text-white ml-2"
                  disabled={busy} onClick={sollecitaIncompleti}
                  title={`Invia il sollecito RAC solo ai ${soggettiIncompleti.length} soggetti con dati mancanti`}>
                  <Send className="w-3.5 h-3.5 mr-1" /> Sollecita solo incompleti ({soggettiIncompleti.length})
                </Button>
              )}
              {/* Iter122ez · Toggle Invio RAC integrato: chip compatto separato da un divisor visivo */}
              <span className="mx-1 h-5 border-l border-slate-300 hidden sm:inline-block" />
              <label className={`inline-flex items-center gap-1.5 cursor-pointer px-2 py-1 rounded-lg border-2 ${invioAnagrafiche ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}
                     data-testid="invio-rac-toggle-label"
                     title={invioAnagrafiche ? "Le email RAC vengono inviate" : "Nessuna email RAC verrà inviata finché non attivi"}>
                <Send className="w-3.5 h-3.5" />
                <span className="text-xs font-bold">Invio {invioAnagrafiche ? "ATTIVO" : "OFF"}</span>
                <input type="checkbox" className="sr-only peer" checked={invioAnagrafiche}
                  onChange={(e) => toggleInvioAnagrafiche(e.target.checked)}
                  data-testid="invio-rac-toggle-input" />
                <div className="relative w-8 h-4 bg-slate-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:rounded-full after:h-[14px] after:w-[14px] after:transition-all peer-checked:bg-emerald-600" />
              </label>
            </div>
          </div>
        </Card>
      )}

      {/* Selettore condominio */}
      <Card className="p-4 mb-4" data-testid="unit-first-controls">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-brand" />
            <span className="font-semibold">Condominio:</span>
          </div>
          <select data-testid="unit-first-cond-select" value={cond} onChange={(e) => setCond(e.target.value)}
            className="h-11 px-3 border-2 border-slate-300 rounded-lg bg-white text-base min-w-[240px]">
            <option value="">— seleziona —</option>
            {condOpts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome} {isMigrated(c.id) ? "✓ migrato" : "· non migrato"}
              </option>
            ))}
          </select>
          <div className="flex-1 min-w-[220px] relative">
            <Search className="w-4 h-4 absolute left-3 top-3.5 text-slate-400" />
            <Input data-testid="unit-first-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per interno, sub, foglio, nome, CF…" className="h-11 pl-9" />
          </div>
          <div className="inline-flex rounded-lg border-2 border-slate-300 overflow-hidden">
            <button data-testid="tab-unita" onClick={() => setTab("unita")}
              className={`h-11 px-4 text-sm font-bold ${tab === "unita" ? "bg-brand text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              <Home className="w-4 h-4 inline mr-1" /> Unità ({unita.length})
            </button>
            <button data-testid="tab-soggetti" onClick={() => setTab("soggetti")}
              className={`h-11 px-4 text-sm font-bold ${tab === "soggetti" ? "bg-brand text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              <Users className="w-4 h-4 inline mr-1" /> Soggetti ({soggetti.length})
            </button>
            <button data-testid="tab-storico" onClick={() => setTab("storico")}
              className={`h-11 px-4 text-sm font-bold ${tab === "storico" ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              <History className="w-4 h-4 inline mr-1" /> Storico ({storico.length})
            </button>
            <button data-testid="tab-cestino" onClick={() => setTab("cestino")}
              className={`h-11 px-4 text-sm font-bold ${tab === "cestino" ? "bg-red-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              <Archive className="w-4 h-4 inline mr-1" /> Cestino ({(cestino.unita?.length || 0) + (cestino.soggetti?.length || 0)})
            </button>
          </div>
          {tab === "unita" && cond && isMigrated(cond) && (
            <Button data-testid="new-unita-btn" className="h-11 bg-brand hover:bg-brand-dark"
              onClick={() => setModal({ type: "unita-new", data: { condominio_id: cond } })}>
              <Plus className="w-4 h-4 mr-1" /> Nuova unità
            </Button>
          )}
          {tab === "soggetti" && (
            <Button data-testid="new-soggetto-btn" className="h-11 bg-brand hover:bg-brand-dark"
              onClick={() => setModal({ type: "soggetto-new", data: {} })}>
              <Plus className="w-4 h-4 mr-1" /> Nuovo soggetto
            </Button>
          )}
        </div>

        {/* Toolbar bulk actions per Vista Nuova migrata */}
        {cond && isMigrated(cond) && (tab === "unita" || tab === "soggetti") && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-200" data-testid="bulk-toolbar">
            <Button data-testid="bulk-rac-pdf-btn" variant="outline" className="h-10 border-brand text-brand"
              onClick={() => setRacModal({ condominio_id: cond, condominio_nome: (condOpts.find((c) => c.id === cond) || {}).nome || "" })}>
              <FileText className="w-4 h-4 mr-1" /> RAC condominio
            </Button>
            <Button data-testid="bulk-sollecita-all-btn" variant="outline" className="h-10 border-amber-500 text-amber-700"
              disabled={busy || soggetti.length === 0} onClick={sollecitaCondominio}>
              <Send className="w-4 h-4 mr-1" /> Sollecita tutti ({soggetti.length})
            </Button>
            <Button data-testid="bulk-export-aggregato-btn" variant="outline" className="h-10"
              disabled={busy} onClick={() => exportExcel("aggregato")}>
              <Download className="w-4 h-4 mr-1" /> Excel aggregato
            </Button>
            <Button data-testid="bulk-export-dettaglio-btn" variant="outline" className="h-10"
              disabled={busy} onClick={() => exportExcel("dettaglio")}>
              <Download className="w-4 h-4 mr-1" /> Excel dettaglio (3 fogli)
            </Button>
            <Button data-testid="bulk-export-danea-btn" variant="outline" className="h-10 border-emerald-400 text-emerald-800"
              disabled={busy} onClick={exportDanea}
              title="Esporta in formato Danea Domus Studio: colonne identiche all'export originale, pronto per re-import in Danea.">
              <Download className="w-4 h-4 mr-1" /> Export Danea
            </Button>
            {/* Iter121g · Pulsante migrazione dati RAC dal legacy proprieta a soggetti_unita */}
            <Button data-testid="bulk-migra-rac-btn" variant="outline" className="h-10 border-sky-400 text-sky-800"
              disabled={busy} onClick={() => setMigraRacModal({ cond_id: cond || null, step: "confirm" })}
              title="Copia i dati RAC (quota, diritto reale, comproprietari, ecc.) dalla vecchia anagrafica al Registro Anagrafico.">
              <Database className="w-4 h-4 mr-1" /> Migra dati RAC
            </Button>
            {/* Iter122gf · Diagnostica e Ripara — orchestrator (strada consigliata) */}
            <Button data-testid="bulk-diagnostica-btn" className="h-10 bg-brand hover:bg-brand-dark text-white"
              disabled={busy} onClick={diagnosticaERipara}
              title="Esegue in sequenza: sub multipli, duplicati soggetti, tipo unità, contatti account. Ti mostra prima cosa cambierà e chiede conferma.">
              <ShieldCheck className="w-4 h-4 mr-1" /> Diagnostica e ripara
            </Button>
            {/* Iter122jt · Toggle "Riparazioni avanzate": mostra/nasconde i 4 pulsanti mirati */}
            <Button data-testid="toggle-riparazioni-avanzate-btn" variant="outline"
              className="h-10 border-slate-300 text-slate-600"
              onClick={() => setShowAdvancedRepairs((v) => !v)}
              title="Riparazioni mirate: normalmente basta usare 'Diagnostica e ripara'. Espandi solo se ti serve eseguire una specifica riparazione singola.">
              <Wrench className="w-4 h-4 mr-1" />
              Riparazioni avanzate {showAdvancedRepairs ? "▴" : "▾"}
            </Button>
            {showAdvancedRepairs && (
              <div className="w-full flex flex-wrap gap-2 pl-2 mt-1 border-l-4 border-slate-200" data-testid="advanced-repairs-panel">
                <div className="basis-full text-[11px] italic text-slate-500 mb-0.5">
                  Riparazioni mirate — usa solo se sai cosa stai facendo. In caso di dubbio, "Diagnostica e ripara" è la strada consigliata.
                </div>
                {/* Iter122fp8 · Ripara sub multipli residui da import Danea pre-fix */}
                <Button data-testid="bulk-ripara-sub-multipli-btn" variant="outline" className="h-10 border-orange-400 text-orange-800"
                  disabled={busy} onClick={riparaSubMultipli}
                  title="Ripara le unità che hanno più subalterni nello stesso campo sub (es. '722, 716'), residuo di import Danea precedenti al fix parser.">
                  <Wrench className="w-4 h-4 mr-1" /> Ripara sub multipli
                </Button>
                {/* Iter122gb + Iter122gc · Migrazione retroattiva email + telefono account → soggetti */}
                <Button data-testid="bulk-sistema-email-btn" variant="outline" className="h-10 border-teal-400 text-teal-800"
                  disabled={busy} onClick={sistemaEmailAccount}
                  title="Scansiona le anagrafiche già sostituite e allinea email e telefono all'account collegato. Sistema le migrazioni fatte prima dei fix Iter122ga/gc.">
                  <Wrench className="w-4 h-4 mr-1" /> Sistema contatti anagrafiche
                </Button>
                {/* Iter122ge · Backfill Tipo unità (Appartamento/Box/Cantina) da proprieta.tipo */}
                <Button data-testid="bulk-backfill-tipo-btn" variant="outline" className="h-10 border-indigo-400 text-indigo-800"
                  disabled={busy} onClick={backfillTipoUnita}
                  title="Popola il campo 'Tipo unità' (Appartamento/Box/Cantina) sulle unità già importate dove è rimasto vuoto.">
                  <Wrench className="w-4 h-4 mr-1" /> Ripara Tipo unità
                </Button>
                {/* Iter122fp10 · Ripara duplicati soggetti (stesso CF + cognome, es. Scotti Paola x3) */}
                <Button data-testid="bulk-ripara-duplicati-soggetti-btn" variant="outline" className="h-10 border-purple-400 text-purple-800"
                  disabled={busy} onClick={riparaDuplicatiSoggetti}
                  title="Identifica soggetti duplicati (stesso codice fiscale e cognome, es. dopo re-import PDF) e li unisce mantenendo il più completo, spostando tutte le relazioni.">
                  <Users className="w-4 h-4 mr-1" /> Ripara duplicati soggetti
                </Button>
              </div>
            )}
            {tab === "soggetti" && selectedSoggetti.size > 0 && (
              <Button data-testid="bulk-sollecita-sel-btn" className="h-10 bg-amber-600 hover:bg-amber-700 text-white"
                disabled={busy} onClick={sollecitaSelezionati}>
                <Send className="w-4 h-4 mr-1" /> Sollecita selezionati ({selectedSoggetti.size})
              </Button>
            )}
            {tab === "unita" && selectedUnita.size > 0 && (
              <Button data-testid="bulk-elimina-unita-btn" className="h-10 bg-red-600 hover:bg-red-700 text-white"
                disabled={busy} onClick={eliminaUnitaSelezionate}>
                <Trash2 className="w-4 h-4 mr-1" /> Elimina unità selezionate ({selectedUnita.size})
              </Button>
            )}
          </div>
        )}
      </Card>

      {!cond && (
        <Card className="p-8 text-center text-slate-500 text-lg" data-testid="no-cond-selected">
          Seleziona un condominio per visualizzare i dati.
        </Card>
      )}

      {/* Iter118 · non mostrare il pannello di migrazione quando l'utente sta consultando
          il cestino di un condominio non-migrato (potrebbe avere solo residui soft-deleted). */}
      {cond && !isMigrated(cond) && tab !== "cestino" && (
        <MigrationPanel cond={cond} condNome={(condOpts.find((c) => c.id === cond) || {}).nome || ""} onMigrated={() => { loadCondomini(); loadData(); }} />
      )}

      {cond && isMigrated(cond) && tab === "unita" && (
        <UnitaTable
          unita={filteredUnita} totale={unita.length} expanded={expanded} toggleExpand={toggleExpand}
          onEdit={(u) => setModal({ type: "unita-edit", data: u })}
          onDelete={eliminaUnita}
          onAddSoggetto={(u) => setModal({ type: "rel-new", data: { unita: u, soggetti_disponibili: soggetti } })}
          onEditRelazione={(u, rel) => setModal({ type: "rel-edit", data: { unita: u, relazione: rel } })}
          onCloseRelazione={chiudiRelazione}
          onSubentro={(u, rel) => setModal({ type: "subentro", data: { unita: u, relazione_uscente: rel || null } })}
          onSetReferente={setReferente}
          onDownloadRacPdf={downloadRacPdfUnita}
          unitaRacStatus={_unitaRacStatus}
          selected={selectedUnita}
          onToggleSelect={toggleSelectUnita}
          onToggleSelectAll={toggleSelectAllUnita}
          aggregatiInfo={aggregatiInfo}
          onSostituisciAggregato={sostituisciAggregato}
          onPurgeRelazione={purgeRelazione}
        />
      )}

      {cond && isMigrated(cond) && tab === "soggetti" && (
        <SoggettiTable
          soggetti={filteredSoggetti} totale={soggetti.length}
          query={query}
          onEdit={(s) => setModal({ type: "soggetto-edit", data: s })}
          onRichiediRAC={richiediRAC}
          onDownloadRacPdf={downloadRacPdfSoggetto}
          selected={selectedSoggetti}
          onToggleSelect={toggleSelectSoggetto}
          onToggleSelectAll={toggleSelectAllSoggetti}
          onDelete={eliminaSoggetto}
          onUploadDoc={(s) => setModal({ type: "soggetto-docs", data: s })}
        />
      )}

      {cond && isMigrated(cond) && tab === "storico" && (
        <StoricoTable query={query} storico={storico.filter((r) => {
          if (!query.trim()) return true;
          const q = norm(query);
          return [r.soggetto_nome, r.codice_fiscale, r.interno, r.sub, r.motivo, r.ruolo].some((v) => norm(v).includes(q));
        })}
        onPurge={async (r) => {
          const dataStr = r.al ? new Date(r.al).toLocaleDateString("it-IT") : "?";
          if (!window.confirm(
            `Eliminare definitivamente questa relazione dallo storico?\n\n` +
            `Soggetto: ${r.soggetto_nome}\nRuolo: ${r.ruolo}\nChiusa il: ${dataStr}\nMotivo: ${r.motivo || "—"}\n\n` +
            `Questa operazione non può essere annullata.`
          )) return;
          try {
            await api.delete(`/refactor/soggetti-unita/${r.relazione_id}/purge`);
            toast.success("Relazione storica eliminata");
            loadData();
          } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Eliminazione non riuscita");
          }
        }} />
      )}

      {/* Iter118 · Il cestino deve essere sempre accessibile quando il condominio ha
          elementi in cestino (indipendentemente dallo stato "migrato" corrente), altrimenti
          gli utenti non riescono a eliminare/ripristinare soggetti soft-deleted di condomini
          "smigrati" o con solo record legacy soft-delete. */}
      {cond && tab === "cestino" && (
        <CestinoTable cestino={cestino} onRipristina={ripristina} onEliminaDefinitivo={eliminaDefinitivo}
          onSvuotaTutto={async () => {
            const nomeCond = (condOpts.find((c) => c.id === cond) || {}).nome || "questo condominio";
            const tot = (cestino.unita?.length || 0) + (cestino.soggetti?.length || 0);
            if (!tot) return;
            if (!window.confirm(`⚠️ ELIMINAZIONE DEFINITIVA — Confermi di svuotare tutto il cestino di ${nomeCond}?\n\n${cestino.unita?.length || 0} unità + ${cestino.soggetti?.length || 0} soggetti verranno cancellati IN MODO IRREVERSIBILE.`)) return;
            try {
              const { data } = await api.post(`/refactor/cestino/svuota?condominio_id=${cond}`);
              toast.success(`Cestino svuotato: ${data.unita_eliminate} unità, ${data.soggetti_eliminati} soggetti, ${data.relazioni_eliminate} relazioni`);
              loadData();
            } catch (e) { toast.error(formatApiError(e)); }
          }} />
      )}

      {racModal && <RacBulkModal target={racModal} onClose={() => setRacModal(null)} />}
      {migraRacModal && (
        <MigraRacModal
          state={migraRacModal}
          onClose={() => setMigraRacModal(null)}
          onRun={runMigraRac}
          busy={busy}
          condominioNome={migraRacModal.cond_id ? (condOpts.find((c) => c.id === migraRacModal.cond_id) || {}).nome : null}
        />
      )}

      {modal && (
        <ModalRouter modal={modal} unitaList={unita} soggettiList={soggetti}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); loadData(); }}
        />
      )}
    </div>
  );
}

/* ============================ TABELLA UNITÀ ============================ */
// Iter122fp5 · Header ordinabili per tutte le colonne principali (interno, catasto,
// scala/piano, tipo, soggetti, referente). Il click sull'header cicla asc → desc →
// nessun ordinamento (ripristina l'ordine originale della lista).
const UNITA_SORT_ACCESSORS = {
  interno: (u) => u.interno || "",
  catasto: (u) => [u.sezione_urbana, u.foglio, u.mappale_particella, u.sub]
    .filter(Boolean).join("/"),
  scala_piano: (u) => [u.scala, u.piano].filter(Boolean).join("/"),
  tipo: (u) => u.tipo_unita || "",
  // Iter122fp7 · Ordinamento alfabetico per cognome (primo token) del primo soggetto
  // collegato — così cercare "Rossi", "Bianchi"… è immediato. Le U.I. senza soggetti
  // finiscono in fondo. Il tag visivo "completa/parziale" NON entra nell'algoritmo.
  soggetti: (u) => {
    const first = (u.soggetti_collegati || [])[0];
    if (!first) return "\uffff"; // sentinel: unità senza soggetti in coda in asc
    return (first.nome || "").trim().toLowerCase();
  },
  referente: (u) => {
    const ref = (u.soggetti_collegati || []).find((s) => s.e_referente_account);
    return ref ? (ref.nome || "").trim().toLowerCase() : "\uffff";
  },
};

function SortHeader({ label, sortKey, currentKey, currentDir, onSort, className }) {
  const active = currentKey === sortKey;
  const Icon = !active ? ChevronsUpDown : (currentDir === "asc" ? ChevronUp : ChevronDown);
  return (
    <th className={`py-2 pr-3 ${className || ""}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        data-testid={`unita-sort-${sortKey}`}
        className={`inline-flex items-center gap-1 uppercase tracking-wide font-bold text-sm hover:text-brand transition-colors ${active ? "text-brand" : "text-slate-500"}`}
        title={active ? (currentDir === "asc" ? "Ordinato crescente — clicca per invertire" : "Ordinato decrescente — clicca per rimuovere ordinamento") : "Clicca per ordinare"}
      >
        <span>{label}</span>
        <Icon className={`w-3.5 h-3.5 ${active ? "opacity-100" : "opacity-50"}`} />
      </button>
    </th>
  );
}

function UnitaTable({ unita, totale, expanded, toggleExpand, onEdit, onDelete, onAddSoggetto, onEditRelazione, onCloseRelazione, onSubentro, onSetReferente, onDownloadRacPdf, unitaRacStatus, selected, onToggleSelect, onToggleSelectAll, aggregatiInfo, onSostituisciAggregato, onPurgeRelazione }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const onSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    // 3° click: rimuove ordinamento e ripristina ordine originale
    setSortKey(null); setSortDir("asc");
  };

  const sortedUnita = useMemo(() => {
    if (!sortKey) return unita;
    const acc = UNITA_SORT_ACCESSORS[sortKey];
    if (!acc) return unita;
    const arr = [...unita];
    const tie = UNITA_SORT_ACCESSORS.interno;
    arr.sort((a, b) => {
      const va = acc(a);
      const vb = acc(b);
      let cmp;
      if (typeof va === "number" && typeof vb === "number") {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb), "it", { numeric: true, sensitivity: "base" });
      }
      if (cmp === 0 && sortKey !== "interno") {
        // Tiebreaker deterministico: Interno crescente sempre, così l'ordine è stabile
        cmp = String(tie(a)).localeCompare(String(tie(b)), "it", { numeric: true, sensitivity: "base" });
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [unita, sortKey, sortDir]);

  if (totale === 0) {
    return <Card className="p-8 text-center text-slate-500" data-testid="unita-empty">
      Nessuna unità presente. Clicca &quot;Nuova unità&quot; per iniziare.
    </Card>;
  }
  if (unita.length === 0) {
    return <Card className="p-8 text-center text-slate-500" data-testid="unita-search-empty">
      Nessun risultato per la ricerca.
    </Card>;
  }
  const allSel = selected && selected.size === unita.length && unita.length > 0;
  return (
    <Card className="p-5 border-2" data-testid="unita-table">
      <div className="overflow-x-auto">
        <table className="w-full text-base">
          <thead>
            <tr className="text-left text-sm font-bold uppercase tracking-wide text-slate-500 border-b">
              <th className="py-2 pr-2 w-6"></th>
              <th className="py-2 pr-2 w-8">
                <input type="checkbox" checked={allSel} onChange={onToggleSelectAll}
                  data-testid="unita-select-all" className="w-4 h-4 accent-brand" title="Seleziona tutte" />
              </th>
              <SortHeader label="Interno" sortKey="interno" currentKey={sortKey} currentDir={sortDir} onSort={onSort} />
              <SortHeader label="Catasto (Sez/Fg/Mp/Sub)" sortKey="catasto" currentKey={sortKey} currentDir={sortDir} onSort={onSort} />
              <SortHeader label="Scala/Piano" sortKey="scala_piano" currentKey={sortKey} currentDir={sortDir} onSort={onSort} />
              <SortHeader label="Tipo" sortKey="tipo" currentKey={sortKey} currentDir={sortDir} onSort={onSort} />
              <SortHeader label="Soggetti" sortKey="soggetti" currentKey={sortKey} currentDir={sortDir} onSort={onSort} />
              <SortHeader label="Referente account" sortKey="referente" currentKey={sortKey} currentDir={sortDir} onSort={onSort} />
              <th className="py-2 w-24">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {sortedUnita.map((u) => {
              const isOpen = expanded.has(u.id);
              const soggAttivi = u.soggetti_collegati || [];
              const storico = u.storico || [];
              const referente = soggAttivi.find((s) => s.e_referente_account);
              const isSel = selected && selected.has(u.id);
              return (
                <Fragment key={u.id}>
                  <tr className={`border-b border-slate-100 hover:bg-slate-50 ${isSel ? "bg-red-50" : ""}`} data-testid={`unita-row-${u.id}`}>
                    <td className="py-2 pr-2">
                      <button onClick={() => toggleExpand(u.id)} className="text-slate-500 hover:text-brand" data-testid={`toggle-${u.id}`}>
                        {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                      </button>
                    </td>
                    <td className="py-2 pr-2">
                      <input type="checkbox" checked={!!isSel} onChange={() => onToggleSelect && onToggleSelect(u.id)}
                        data-testid={`unita-check-${u.id}`} className="w-4 h-4 accent-brand" title="Seleziona questa unità" />
                    </td>
                    <td className="py-2 pr-3 font-bold text-brand">
                      <Home className="w-4 h-4 inline mr-1" /> {u.interno || "—"}
                    </td>
                    <td className="py-2 pr-3 text-sm text-slate-700">
                      {[u.sezione_urbana, u.foglio, u.mappale_particella, u.sub].filter(Boolean).join(" / ") || <span className="text-slate-400">—</span>}
                      {u.comune_catastale && <div className="text-xs text-slate-500">{u.comune_catastale}</div>}
                    </td>
                    <td className="py-2 pr-3 text-sm">{[u.scala, u.piano].filter(Boolean).join(" / ") || "—"}</td>
                    <td className="py-2 pr-3 text-sm"><TipoBadge tipo={u.tipo_unita} /></td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-wrap gap-1 items-center">
                        {soggAttivi.length === 0 ? (
                          <span className="text-xs text-slate-400 italic">nessuno</span>
                        ) : soggAttivi.slice(0, 3).map((s) => (
                          <span key={s.relazione_id} className={`inline-block px-2 py-0.5 rounded border text-xs font-semibold ${ruoloTone(s.ruolo)}`}
                            title={`${s.nome} · ${s.ruolo}`}>
                            {s.nome.split(" ").slice(0, 2).join(" ")}
                          </span>
                        ))}
                        {soggAttivi.length > 3 && <span className="text-xs text-slate-500">+{soggAttivi.length - 3}</span>}
                        {/* Iter121h · pill "completa"/"parziale" affiancata ai soggetti */}
                        {(() => {
                          const st = unitaRacStatus ? unitaRacStatus(u) : "nessuno";
                          if (st === "completo") return (
                            <span data-testid={`unita-rac-completa-${u.id}`}
                              className="inline-flex items-center gap-0.5 ml-1 px-1.5 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-800 text-[10px] font-bold uppercase tracking-wide"
                              title="Tutti i soggetti collegati hanno dati RAC completi">
                              ✓ completa
                            </span>
                          );
                          if (st === "parziale") return (
                            <span data-testid={`unita-rac-parziale-${u.id}`}
                              className="inline-flex items-center gap-0.5 ml-1 px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-800 text-[10px] font-bold uppercase tracking-wide"
                              title="Alcuni soggetti hanno dati RAC completi, altri no">
                              parziale
                            </span>
                          );
                          return null;
                        })()}
                      </div>
                    </td>
                    <td className="py-2 pr-3">
                      {referente ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-sm font-semibold" data-testid={`referente-${u.id}`}>
                          <Star className="w-4 h-4 fill-emerald-500 text-emerald-500" /> {referente.nome}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-600 text-xs">
                          <StarOff className="w-4 h-4" /> nessuno
                        </span>
                      )}
                    </td>
                    <td className="py-2">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-sky-700" onClick={() => onDownloadRacPdf && onDownloadRacPdf(u.id, u.interno || u.sub)} data-testid={`unita-rac-pdf-${u.id}`} title="Scarica PDF RAC di questa unità">
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-brand" onClick={() => onEdit(u)} data-testid={`unita-edit-${u.id}`} title="Modifica unità">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => onDelete(u)} data-testid={`unita-del-${u.id}`} title="Elimina unità">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr key={`${u.id}-detail`} className="bg-slate-50" data-testid={`unita-detail-${u.id}`}>
                      <td colSpan={9} className="py-3 px-4">
                        {/* Iter122fw · Banner Aggregati candidati per l'unità corrente
                            Iter122fp10 · Ora con azione INLINE "Usa come sostituto" per ogni candidato
                            (non serve più andare in Importa Dati). */}
                        {(aggregatiInfo?.byUnita?.[u.id] || []).length > 0 && (
                          <div data-testid={`unita-aggregati-banner-${u.id}`}
                            className="mb-3 rounded-lg border-2 border-indigo-300 bg-indigo-50 px-3 py-2 text-xs text-indigo-900">
                            <div className="font-bold flex items-center gap-1.5 mb-1">🔀 Aggregato rilevato su questa unità</div>
                            {(aggregatiInfo.byUnita[u.id]).map((a, i) => (
                              <div key={i} className="mb-2 pb-2 border-b border-indigo-200 last:border-b-0 last:pb-0">
                                <div className="mb-1">
                                  Il soggetto <b>&quot;{a.aggregato_nome}&quot;</b>{a.aggregato_ha_account ? " (con account)" : ""} sembra un aggregato.
                                </div>
                                {(a.candidati || []).length === 0 ? (
                                  <div className="italic text-indigo-700">Nessun candidato individuale trovato su questa unità.</div>
                                ) : (
                                  <div className="space-y-1">
                                    <div className="text-[11px] text-indigo-800">Sostituiscilo con un individuo di questa unità:</div>
                                    {(a.candidati || []).map((c) => (
                                      <div key={c.soggetto_id} className="flex items-center justify-between gap-2 bg-white/60 rounded px-2 py-1">
                                        <span className="font-semibold text-slate-800">{c.nome}{c.codice_fiscale ? <span className="text-[10px] text-slate-500 ml-1">CF: {c.codice_fiscale}</span> : null}</span>
                                        <Button size="sm" variant="outline"
                                          data-testid={`banner-sostituisci-${a.aggregato_id}-${c.soggetto_id}`}
                                          className="h-7 text-[11px] border-indigo-400 text-indigo-800 hover:bg-indigo-100"
                                          onClick={() => onSostituisciAggregato && onSostituisciAggregato(a, c)}
                                          title="Sposta account/documenti dell'aggregato su questo individuo e archivia l'aggregato">
                                          Usa come sostituto
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="mb-3 flex items-center justify-between">
                          <div className="font-bold text-slate-700 flex items-center gap-2">
                            <User className="w-4 h-4" /> Soggetti collegati ({soggAttivi.length})
                          </div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" className="h-8 border-amber-400 text-amber-700 hover:bg-amber-50" onClick={() => onSubentro && onSubentro(u, null)} data-testid={`subentro-btn-${u.id}`} title="Registra vendita/subentro (chiude uscente e apre subentrante in un solo passaggio)">
                              <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Subentro/Vendita
                            </Button>
                            <Button size="sm" className="h-8 bg-brand hover:bg-brand-dark" onClick={() => onAddSoggetto(u)} data-testid={`add-rel-${u.id}`}>
                              <Link2 className="w-3.5 h-3.5 mr-1" /> Collega soggetto
                            </Button>
                          </div>
                        </div>
                        {soggAttivi.length === 0 ? (
                          <div className="text-slate-500 text-sm italic mb-3">Nessun soggetto attivo su questa unità.</div>
                        ) : (
                          /* Iter122ez · Card leggibili per ogni soggetto collegato (comproprietari + altri titolari di diritti reali).
                             Prima era una tabella stretta che nascondeva molti campi RAC — ora ogni soggetto ha le stesse info del referente. */
                          <div className="grid gap-3" data-testid={`soggetti-collegati-list-${u.id}`}>
                            {soggAttivi.map((s) => {
                              const isCond = s.ruolo === "Inquilino/Conduttore";
                              const racOk = isCond
                                ? (s.inizio_locazione && s.fine_locazione && s.num_occupanti)
                                : (s.quota && s.diritto_reale && s.tipo_casa && s.percentuale_detrazione && s.num_occupanti);
                              const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT") : "—";
                              return (
                                <div key={s.relazione_id} data-testid={`rel-row-${s.relazione_id}`}
                                  className={`rounded-lg border-2 p-3 bg-white ${s.e_referente_account ? "border-emerald-300 bg-emerald-50/30" : "border-slate-200"}`}>
                                  {/* Header: nome + badge ruolo + azioni */}
                                  <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span
                                        title={racOk ? "Dati RAC completi" : "Dati RAC incompleti — clicca ✏ per compilare"}
                                        data-testid={`rac-status-${s.relazione_id}`}
                                        className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${racOk ? "bg-emerald-500" : "bg-amber-500"}`}
                                      />
                                      <span className="font-bold text-slate-800">{s.nome}</span>
                                      {s.e_referente_account && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-700 text-[11px] font-bold">
                                          <Star className="w-3 h-3 fill-emerald-500 text-emerald-500" /> Referente
                                        </span>
                                      )}
                                      <span className={`inline-block px-2 py-0.5 rounded border text-xs font-semibold ${ruoloTone(s.ruolo)}`}>{s.ruolo}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      {!s.e_referente_account && RUOLI_PROPR.includes(s.ruolo) && (
                                        <button onClick={() => onSetReferente(s.relazione_id)}
                                          className="text-slate-400 hover:text-emerald-600 p-1" title="Imposta come referente account"
                                          data-testid={`set-ref-${s.relazione_id}`}>
                                          <StarOff className="w-4 h-4" />
                                        </button>
                                      )}
                                      <Button variant="ghost" size="icon" className="h-7 w-7 text-brand" onClick={() => onEditRelazione(u, s)} data-testid={`rel-edit-${s.relazione_id}`} title="Modifica relazione">
                                        <Pencil className="w-3.5 h-3.5" />
                                      </Button>
                                      {RUOLI_PROPR.includes(s.ruolo) && (
                                        <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-700" onClick={() => onSubentro && onSubentro(u, s)} data-testid={`rel-subentro-${s.relazione_id}`} title="Registra subentro/vendita da questo titolare">
                                          <ArrowRightLeft className="w-3.5 h-3.5" />
                                        </Button>
                                      )}
                                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => onCloseRelazione(s.relazione_id)} data-testid={`rel-close-${s.relazione_id}`} title="Chiudi relazione (archivia)">
                                        <X className="w-3.5 h-3.5" />
                                      </Button>
                                    </div>
                                  </div>

                                  {/* Griglia informazioni RAC — stessa struttura per tutti i soggetti */}
                                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1.5 text-xs">
                                    <div><span className="text-slate-500">Codice fiscale</span><div className="font-mono text-slate-800">{s.cf || "—"}</div></div>
                                    <div><span className="text-slate-500">Diritto reale</span><div className="text-slate-800">{s.diritto_reale || "—"}</div></div>
                                    <div><span className="text-slate-500">Quota</span><div className="text-slate-800 font-semibold">{s.quota ? `${s.quota}%` : "—"}</div></div>
                                    <div><span className="text-slate-500">Tipologia possesso</span><div className="text-slate-800">{s.tipo_casa || "—"}</div></div>
                                    <div><span className="text-slate-500">% detrazione</span><div className="text-slate-800">{s.percentuale_detrazione ? `${s.percentuale_detrazione}%` : "—"}</div></div>
                                    <div><span className="text-slate-500">N. occupanti</span><div className="text-slate-800">{s.num_occupanti || "—"}</div></div>
                                    <div><span className="text-slate-500">Email</span><div className="text-slate-800 break-all">{s.email || "—"}</div></div>
                                    <div><span className="text-slate-500">Telefono</span><div className="text-slate-800">{s.telefono || "—"}</div></div>
                                    <div><span className="text-slate-500">Dal</span><div className="text-slate-800">{fmtDate(s.dal)}</div></div>
                                    {isCond && (<>
                                      <div><span className="text-slate-500">Inizio locazione</span><div className="text-slate-800">{fmtDate(s.inizio_locazione)}</div></div>
                                      <div><span className="text-slate-500">Fine locazione</span><div className="text-slate-800">{fmtDate(s.fine_locazione)}</div></div>
                                    </>)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {storico.length > 0 && (
                          <div className="mt-4">
                            <div className="font-bold text-slate-700 mb-2 text-sm">Storico titolari ({storico.length})</div>
                            <table className="w-full text-xs border rounded overflow-hidden bg-white">
                              <thead className="bg-slate-100 text-slate-500 uppercase">
                                <tr>
                                  <th className="p-2 text-left">Nome</th><th className="p-2 text-left">Ruolo</th>
                                  <th className="p-2 text-left">Dal</th><th className="p-2 text-left">Al</th>
                                  <th className="p-2 text-left">Motivo</th>
                                  <th className="p-2 w-10"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {storico.map((s) => (
                                  <tr key={s.relazione_id} className="border-b">
                                    <td className="p-2 text-slate-600">{s.nome}</td>
                                    <td className="p-2 text-slate-500">{s.ruolo}</td>
                                    <td className="p-2 text-slate-500">{s.dal ? new Date(s.dal).toLocaleDateString("it-IT") : "—"}</td>
                                    <td className="p-2 text-slate-500">{s.al ? new Date(s.al).toLocaleDateString("it-IT") : "—"}</td>
                                    <td className="p-2 text-slate-500">{s.motivo || "—"}</td>
                                    <td className="p-2 text-right">
                                      <button
                                        data-testid={`storico-inline-purge-${s.relazione_id}`}
                                        className="text-red-600 hover:bg-red-50 p-1 rounded"
                                        title="Elimina definitivamente questa relazione chiusa"
                                        onClick={() => onPurgeRelazione && onPurgeRelazione(s)}
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ============================ TABELLA SOGGETTI ============================ */
function SoggettiTable({ soggetti, totale, query = "", onEdit, onRichiediRAC, onDownloadRacPdf, selected, onToggleSelect, onToggleSelectAll, onDelete, onUploadDoc }) {
  if (totale === 0) {
    return <Card className="p-8 text-center text-slate-500" data-testid="soggetti-empty">
      Nessun soggetto in questo condominio.
    </Card>;
  }
  if (soggetti.length === 0) {
    return <Card className="p-8 text-center text-slate-500">Nessun risultato.</Card>;
  }
  const allSel = selected.size === soggetti.length && soggetti.length > 0;
  return (
    <Card className="p-5 border-2" data-testid="soggetti-table">
      <div className="overflow-x-auto">
        <table className="w-full text-base">
          <thead>
            <tr className="text-left text-sm font-bold uppercase tracking-wide text-slate-500 border-b">
              <th className="py-2 pr-2 w-8">
                <input type="checkbox" checked={allSel} onChange={onToggleSelectAll} data-testid="soggetti-select-all" className="w-4 h-4 accent-brand" />
              </th>
              <th className="py-2 pr-3">Nome</th>
              <th className="py-2 pr-3">CF</th>
              <th className="py-2 pr-3">Email / Tel</th>
              <th className="py-2 pr-3">Unità</th>
              <th className="py-2 pr-3">Docs</th>
              <th className="py-2 pr-3">Account</th>
              <th className="py-2 w-32">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {soggetti.map((s) => (
              <tr key={s.id} className={`border-b border-slate-100 hover:bg-slate-50 ${selected.has(s.id) ? "bg-amber-50" : ""}`} data-testid={`soggetto-row-${s.id}`}>
                <td className="py-2 pr-2">
                  <input type="checkbox" checked={selected.has(s.id)} onChange={() => onToggleSelect(s.id)} data-testid={`soggetto-check-${s.id}`} className="w-4 h-4 accent-brand" />
                </td>
                <td className="py-2 pr-3 font-semibold">
                  {s.is_ex && <span className="mr-1 inline-block px-1.5 py-0.5 text-[10px] rounded bg-slate-800 text-white font-bold">EX</span>}
                  {highlightMatch(s.nome, query)}
                </td>
                <td className="py-2 pr-3 text-xs">{s.codice_fiscale ? highlightMatch(s.codice_fiscale, query) : "—"}</td>
                <td className="py-2 pr-3 text-xs">
                  {s.email && <div>{highlightMatch(s.email, query)}</div>}
                  {s.telefono && <div className="text-slate-500">{highlightMatch(s.telefono, query)}</div>}
                  {!s.email && !s.telefono && "—"}
                </td>
                <td className="py-2 pr-3">
                  <div className="flex flex-wrap gap-1">
                    {(s.unita_collegate || []).slice(0, 3).map((u) => (
                      <span key={u.relazione_id} className={`inline-block px-2 py-0.5 rounded border text-xs font-semibold ${ruoloTone(u.ruolo)}`}
                        title={`${u.condominio_nome} · int. ${u.interno || u.sub} · ${u.ruolo}`}>
                        {u.interno || `sub ${u.sub}`} {u.e_referente_account && <Star className="w-3 h-3 inline fill-emerald-500 text-emerald-500" />}
                      </span>
                    ))}
                    {(s.unita_collegate || []).length === 0 && <span className="text-xs text-slate-400 italic">nessuna</span>}
                    {(s.unita_collegate || []).length > 3 && <span className="text-xs text-slate-500">+{(s.unita_collegate || []).length - 3}</span>}
                  </div>
                </td>
                <td className="py-2 pr-3 text-xs">
                  {(s.documenti || []).length > 0 ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold" title={(s.documenti || []).map(d => d.filename).join(", ")}>
                      <Paperclip className="w-3 h-3" />{(s.documenti || []).length}
                    </span>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="py-2 pr-3 text-xs">
                  {s.has_account
                    ? <span className="inline-block px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">Attivo</span>
                    : <span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-600">No account</span>}
                </td>
                <td className="py-2">
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-brand" onClick={() => onEdit(s)} data-testid={`soggetto-edit-${s.id}`} title="Modifica anagrafica">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600" onClick={() => onUploadDoc(s)} data-testid={`soggetto-docs-${s.id}`} title="Documenti">
                      <Paperclip className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-sky-700" onClick={() => onDownloadRacPdf && onDownloadRacPdf(s.id, s.nome)} data-testid={`soggetto-rac-pdf-${s.id}`} title="Scarica PDF RAC (tutte le unità di questo soggetto)">
                      <FileText className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600" onClick={() => onRichiediRAC(s)} data-testid={`soggetto-rac-${s.id}`} title="Richiedi RAC (aggiornamento dati)">
                      <Mail className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => onDelete(s)} data-testid={`soggetto-del-${s.id}`} title="Sposta nel cestino">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ============================ TABELLA STORICO ============================ */
function StoricoTable({ storico, query = "", onPurge }) {
  if (storico.length === 0) {
    return <Card className="p-8 text-center text-slate-500" data-testid="storico-empty">
      <History className="w-12 h-12 mx-auto text-slate-300 mb-2" />
      Nessuna relazione chiusa. Le relazioni che verranno chiuse (subentri, vendite, cambi inquilino) appariranno qui.
    </Card>;
  }
  return (
    <Card className="p-5 border-2 border-slate-300" data-testid="storico-table">
      <div className="mb-3 text-sm text-slate-600">
        <b>Storico titolari</b> — {storico.length} relazioni chiuse. Utile per tracciare vendite, subentri, cambi inquilino.
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-base">
          <thead>
            <tr className="text-left text-sm font-bold uppercase tracking-wide text-slate-500 border-b">
              <th className="py-2 pr-3">Soggetto</th>
              <th className="py-2 pr-3">CF</th>
              <th className="py-2 pr-3">Unità</th>
              <th className="py-2 pr-3">Ruolo</th>
              <th className="py-2 pr-3">Dal → Al</th>
              <th className="py-2 pr-3">Motivo</th>
              <th className="py-2 pr-3 w-14"></th>
            </tr>
          </thead>
          <tbody>
            {storico.map((r) => (
              <tr key={r.relazione_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`storico-row-${r.relazione_id}`}>
                <td className="py-2 pr-3 font-semibold">{highlightMatch(r.soggetto_nome, query)}</td>
                <td className="py-2 pr-3 text-xs">{r.codice_fiscale ? highlightMatch(r.codice_fiscale, query) : "—"}</td>
                <td className="py-2 pr-3 text-sm">{r.interno ? highlightMatch(r.interno, query) : (r.sub ? <>sub {highlightMatch(String(r.sub), query)}</> : "—")}</td>
                <td className="py-2 pr-3 text-xs"><span className={`inline-block px-2 py-0.5 rounded border font-semibold ${ruoloTone(r.ruolo)}`}>{highlightMatch(r.ruolo, query)}</span></td>
                <td className="py-2 pr-3 text-xs">
                  <div>{r.dal ? new Date(r.dal).toLocaleDateString("it-IT") : "—"}</div>
                  <div className="text-slate-400">↓ {r.al ? new Date(r.al).toLocaleDateString("it-IT") : "—"}</div>
                </td>
                <td className="py-2 pr-3 text-xs italic">{r.motivo ? highlightMatch(r.motivo, query) : "—"}</td>
                <td className="py-2 pr-3 text-center">
                  <button
                    data-testid={`storico-purge-${r.relazione_id}`}
                    className="text-red-600 hover:bg-red-50 p-2 rounded transition-colors"
                    title="Elimina definitivamente questa relazione chiusa"
                    onClick={() => onPurge && onPurge(r)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ============================ CESTINO ============================ */
function CestinoTable({ cestino, onRipristina, onEliminaDefinitivo, onSvuotaTutto }) {
  const tot = (cestino.unita?.length || 0) + (cestino.soggetti?.length || 0);
  if (tot === 0) {
    return <Card className="p-8 text-center text-slate-500" data-testid="cestino-empty">
      <Archive className="w-12 h-12 mx-auto text-slate-300 mb-2" />
      Cestino vuoto.
    </Card>;
  }
  return (
    <div className="space-y-4" data-testid="cestino-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm text-slate-600 flex-1 min-w-[220px]">
          Gli elementi qui possono essere ripristinati o eliminati definitivamente. L&apos;eliminazione definitiva è irreversibile.
        </div>
        {onSvuotaTutto && (
          <Button size="sm" variant="outline" data-testid="cestino-svuota-tutto"
            className="h-8 border-red-400 text-red-800 hover:bg-red-50 font-bold"
            onClick={onSvuotaTutto}>
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Svuota cestino ({tot})
          </Button>
        )}
      </div>
      {(cestino.unita || []).length > 0 && (
        <Card className="p-4 border-2 border-red-200">
          <div className="font-bold text-red-800 mb-2">Unità eliminate ({cestino.unita.length})</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500 border-b">
                <th className="py-1">Interno / Sub</th><th>Condominio</th><th>Data</th><th>Motivo</th><th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {cestino.unita.map((u) => (
                <tr key={u.id} className="border-b border-slate-100" data-testid={`cestino-unita-row-${u.id}`}>
                  <td className="py-2 font-semibold">{u.interno || `sub ${u.sub}`}</td>
                  <td className="py-2 text-xs">{u.condominio_nome}</td>
                  <td className="py-2 text-xs text-slate-500">{u.deleted_at ? new Date(u.deleted_at).toLocaleString("it-IT") : "—"}</td>
                  <td className="py-2 text-xs italic">{u.deleted_reason}</td>
                  <td className="py-2 flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-700" onClick={() => onRipristina("unita", u.id)} data-testid={`restore-unita-${u.id}`} title="Ripristina">
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-700" onClick={() => onEliminaDefinitivo("unita", u.id)} data-testid={`hard-del-unita-${u.id}`} title="Elimina definitivamente">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      {(cestino.soggetti || []).length > 0 && (
        <Card className="p-4 border-2 border-red-200">
          <div className="font-bold text-red-800 mb-2">Soggetti eliminati ({cestino.soggetti.length})</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500 border-b">
                <th className="py-1">Nome</th><th>CF</th><th>Data</th><th>Motivo</th><th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {cestino.soggetti.map((s) => (
                <tr key={s.id} className="border-b border-slate-100" data-testid={`cestino-sogg-row-${s.id}`}>
                  <td className="py-2 font-semibold">{s.nome}</td>
                  <td className="py-2 text-xs">{s.codice_fiscale || "—"}</td>
                  <td className="py-2 text-xs text-slate-500">{s.deleted_at ? new Date(s.deleted_at).toLocaleString("it-IT") : "—"}</td>
                  <td className="py-2 text-xs italic">{s.deleted_reason}</td>
                  <td className="py-2 flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-700" onClick={() => onRipristina("soggetto", s.id)} data-testid={`restore-sogg-${s.id}`} title="Ripristina">
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-700" onClick={() => onEliminaDefinitivo("soggetto", s.id)} data-testid={`hard-del-sogg-${s.id}`} title="Elimina definitivamente">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

/* ============================ MIGRA DATI RAC MODAL (Iter121g) ============================ */
function MigraRacModal({ state, onClose, onRun, busy, condominioNome }) {
  const { step, report } = state;
  const isGlobal = !state.cond_id;
  return (
    <ModalShell title={`Migra dati RAC ${isGlobal ? "(TUTTI i condomini)" : `— ${condominioNome || ""}`}`} onClose={onClose} testid="migra-rac-modal">
      {step === "confirm" && (
        <div className="space-y-3">
          <div className="text-sm text-slate-700">
            Questa operazione copia i dati anagrafici RAC (quota, diritto reale, tipo casa, occupanti, detrazione,
            comproprietari, locazione) dalla <b>vecchia sezione &quot;Anagrafica&quot; (collezione <code>proprieta</code>)</b>
            al <b>Registro Anagrafico (<code>soggetti_unita</code>)</b>.
          </div>
          <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            <b>✓ Sicura</b> — idempotente e non distruttiva:
            <ul className="list-disc ml-6 mt-1 text-xs">
              <li>Non sovrascrive i campi già valorizzati nel nuovo modello</li>
              <li>Non elimina/modifica i dati nella vecchia sezione</li>
              <li>Puoi rilanciarla più volte senza duplicati</li>
              <li>I comproprietari con CF che matcha un soggetto esistente diventano relazioni pulite; gli altri restano come titolari secondari inline</li>
            </ul>
          </div>
          <div className="text-xs text-slate-500">
            {isGlobal
              ? "Ambito: TUTTI i condomini di questo studio."
              : `Ambito: solo il condominio "${condominioNome}".`}
          </div>
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            💡 Prima di eseguire, lancia un <b>Dry-run</b> per vedere il report senza scrivere nulla.
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Annulla</Button>
            <Button variant="outline" data-testid="migra-dry-run" disabled={busy}
              onClick={() => onRun(true)} className="border-sky-400 text-sky-800">
              <FlaskConical className="w-4 h-4 mr-1" /> Dry-run (anteprima)
            </Button>
            <Button data-testid="migra-esegui-diretto" disabled={busy}
              className="bg-brand hover:bg-brand-dark text-white"
              onClick={() => onRun(false)}>
              <Database className="w-4 h-4 mr-1" /> Esegui migrazione
            </Button>
          </div>
        </div>
      )}

      {(step === "preview" || step === "done") && report && (
        <div className="space-y-3">
          <div className={`p-3 rounded-lg border-2 ${step === "done" ? "border-emerald-300 bg-emerald-50" : "border-sky-300 bg-sky-50"}`}>
            <div className={`font-bold ${step === "done" ? "text-emerald-900" : "text-sky-900"}`}>
              {step === "done" ? "Migrazione completata" : "Report Dry-run (nessuna scrittura)"}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
              <div className="bg-white rounded p-2 border">
                <div className="text-xs text-slate-500">Proprietà legacy processate</div>
                <div className="text-lg font-bold text-slate-800" data-testid="report-processed">{report.processed || 0}</div>
              </div>
              <div className="bg-white rounded p-2 border">
                <div className="text-xs text-slate-500">Relazioni aggiornate</div>
                <div className="text-lg font-bold text-emerald-700" data-testid="report-aggiornate">{report.relazioni_aggiornate || 0}</div>
              </div>
              <div className="bg-white rounded p-2 border">
                <div className="text-xs text-slate-500">Titolari promossi a soggetto</div>
                <div className="text-lg font-bold text-sky-700" data-testid="report-promossi">{report.titolari_promossi_a_soggetto || 0}</div>
              </div>
              <div className="bg-white rounded p-2 border">
                <div className="text-xs text-slate-500">Titolari inline (senza CF match)</div>
                <div className="text-lg font-bold text-slate-700" data-testid="report-inline">{report.titolari_inline || 0}</div>
              </div>
              {(report.relazioni_non_trovate || 0) > 0 && (
                <div className="col-span-2 bg-amber-50 rounded p-2 border border-amber-300">
                  <div className="text-xs text-amber-800 font-semibold">
                    ⚠ {report.relazioni_non_trovate} proprietà non hanno un match nel Registro Anagrafico
                  </div>
                  <details className="mt-1 text-xs">
                    <summary className="cursor-pointer text-amber-700">Dettaglio</summary>
                    <ul className="mt-1 pl-3 max-h-40 overflow-y-auto">
                      {(report.dettaglio_non_trovate || []).slice(0, 30).map((r, i) => (
                        <li key={i}>· {r.nome} <span className="text-slate-500">({r.condominio_nome})</span></li>
                      ))}
                      {(report.dettaglio_non_trovate || []).length > 30 && <li className="italic text-slate-500">…e altri {(report.dettaglio_non_trovate || []).length - 30}</li>}
                    </ul>
                    <div className="mt-1 text-slate-600">Suggerimento: probabilmente questi condomini non sono ancora migrati al Registro Anagrafico. Migrali prima dall&apos;anagrafica e ri-esegui.</div>
                  </details>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            {step === "preview" ? (
              <>
                <Button variant="outline" onClick={onClose}>Annulla</Button>
                <Button data-testid="migra-esegui" disabled={busy}
                  className="bg-brand hover:bg-brand-dark text-white"
                  onClick={() => onRun(false)}>
                  <Database className="w-4 h-4 mr-1" /> Esegui davvero
                </Button>
              </>
            ) : (
              <Button onClick={onClose}>Chiudi</Button>
            )}
          </div>
        </div>
      )}
    </ModalShell>
  );
}


/* ============================ RAC BULK MODAL ============================ */
function RacBulkModal({ target, onClose }) {
  const { condominio_id, condominio_nome } = target;
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const doPdf = async (inline) => {
    setBusy(true);
    try {
      const r = await api.get(`/refactor/condomini/${condominio_id}/rac/pdf`, { params: { inline: !!inline }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (inline) window.open(url, "_blank");
      else {
        const a = document.createElement("a");
        a.href = url; a.download = `RAC_${condominio_nome.replace(/\s+/g, "_")}.pdf`; a.click();
      }
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore generazione RAC"); }
    finally { setBusy(false); }
  };
  const doEmail = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/refactor/condomini/${condominio_id}/rac/email`, { email: email || undefined });
      toast.success(`RAC inviato a ${data.email}`);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore invio RAC"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="rac-bulk-modal" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-bold text-xl flex items-center gap-2"><FileText className="w-6 h-6 text-brand" /> RAC — {condominio_nome}</h3>
          <button onClick={onClose}><X className="w-6 h-6" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-sm text-slate-600">Il PDF aggrega 1 scheda per soggetto (con tutte le sue unità).</div>
          <Button data-testid="rac-bulk-view" disabled={busy} onClick={() => doPdf(true)} className="w-full h-12 bg-brand hover:bg-brand-dark">
            <FileText className="w-5 h-5 mr-2" /> Visualizza PDF
          </Button>
          <Button data-testid="rac-bulk-download" disabled={busy} onClick={() => doPdf(false)} variant="outline" className="w-full h-12">
            <Download className="w-5 h-5 mr-2" /> Scarica PDF
          </Button>
          <div className="pt-3 border-t">
            <Label className="text-sm">Invia il PDF via email a:</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="lascia vuoto per email dello Studio" className="mt-1" data-testid="rac-bulk-email-input" />
            <Button data-testid="rac-bulk-email-send" disabled={busy} onClick={doEmail} variant="outline" className="w-full h-11 mt-2 border-green-300 text-green-800">
              <Send className="w-4 h-4 mr-2" /> Invia via email
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================ MODAL ROUTER ============================ */
function ModalRouter({ modal, unitaList, soggettiList, onClose, onSaved }) {
  if (modal.type === "unita-new" || modal.type === "unita-edit") {
    return <UnitaModal isNew={modal.type === "unita-new"} data={modal.data} onClose={onClose} onSaved={onSaved} />;
  }
  if (modal.type === "soggetto-new" || modal.type === "soggetto-edit") {
    return <SoggettoModal isNew={modal.type === "soggetto-new"} data={modal.data} onClose={onClose} onSaved={onSaved} />;
  }
  if (modal.type === "rel-new") {
    return <RelazioneModal unita={modal.data.unita} soggetti={soggettiList} onClose={onClose} onSaved={onSaved} />;
  }
  if (modal.type === "rel-edit") {
    return <RelazioneEditModal unita={modal.data.unita} relazione={modal.data.relazione} onClose={onClose} onSaved={onSaved} />;
  }
  if (modal.type === "subentro") {
    return <SubentroModal unita={modal.data.unita} relazioneUscente={modal.data.relazione_uscente} soggetti={soggettiList} onClose={onClose} onSaved={onSaved} />;
  }
  if (modal.type === "soggetto-docs") {
    return <SoggettoDocsModal soggetto={modal.data} onClose={onClose} onSaved={onSaved} />;
  }
  return null;
}

/* ============================ SOGGETTO DOCS MODAL ============================ */
function SoggettoDocsModal({ soggetto, onClose, onSaved }) {
  const [docs, setDocs] = useState(soggetto.documenti || []);
  const [tipo, setTipo] = useState("identita");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/refactor/soggetti/${soggetto.id}/documento?tipo=${tipo}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocs(data.documenti || []);
      toast.success("Documento caricato");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload non riuscito");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const rimuovi = async (fid) => {
    if (!window.confirm("Rimuovere questo documento?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/refactor/soggetti/${soggetto.id}/documento/${fid}`);
      setDocs(data.documenti || []);
      toast.success("Rimosso");
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title={`Documenti — ${soggetto.nome}`} onClose={onClose} testid="soggetto-docs-modal">
      <div className="space-y-4">
        <div className="p-4 border-2 border-dashed border-slate-300 rounded-lg bg-slate-50">
          <div className="flex flex-wrap items-center gap-2">
            <Label className="text-sm font-semibold">Tipo:</Label>
            <select data-testid="doc-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}
              className="h-10 px-3 border-2 border-slate-300 rounded-lg bg-white text-sm">
              <option value="identita">Documento identità</option>
              <option value="codice_fiscale">Codice fiscale</option>
              <option value="contratto">Contratto</option>
              <option value="attestazione_oneri">Attestazione oneri</option>
              <option value="altro">Altro</option>
            </select>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={upload}
              data-testid="doc-upload-input" className="hidden" />
            <Button data-testid="doc-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()}
              className="h-10 bg-brand hover:bg-brand-dark">
              <Upload className="w-4 h-4 mr-1" /> Carica file
            </Button>
          </div>
          <div className="text-xs text-slate-500 mt-2">PDF o immagini · max 20 MB</div>
        </div>
        {docs.length === 0 ? (
          <div className="text-center text-slate-400 text-sm py-6">Nessun documento allegato.</div>
        ) : (
          <ul className="divide-y" data-testid="doc-list">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center justify-between py-2" data-testid={`doc-item-${d.id}`}>
                <div>
                  <div className="text-sm font-semibold flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-slate-500" /> {d.filename}
                  </div>
                  <div className="text-xs text-slate-500">{d.tipo} · caricato {d.uploaded_at ? new Date(d.uploaded_at).toLocaleString("it-IT") : ""}</div>
                </div>
                <div className="flex gap-1">
                  <button type="button" onClick={() => openSecureFile(`/files/${d.id}`)}
                    data-testid={`doc-open-${d.id}`}
                    className="text-brand h-8 w-8 flex items-center justify-center hover:bg-slate-100 rounded" title="Apri">
                    <Download className="w-4 h-4" />
                  </button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" disabled={busy}
                    onClick={() => rimuovi(d.id)} data-testid={`doc-del-${d.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid={testid} onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="font-bold text-xl">{title}</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800" data-testid="modal-close"><X className="w-6 h-6" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

/* ---------- UNITA MODAL ---------- */
function UnitaModal({ isNew, data, onClose, onSaved }) {
  const [f, setF] = useState({
    condominio_id: data.condominio_id || "",
    interno: data.interno || "", palazzina: data.palazzina || "",
    scala: data.scala || "", piano: data.piano || "",
    tipo_unita: data.tipo_unita || "",
    comune_catastale: data.comune_catastale || "",
    sezione_urbana: data.sezione_urbana || "",
    foglio: data.foglio || "", mappale_particella: data.mappale_particella || "",
    sub: data.sub || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.sub.trim()) { toast.error("Il sub è obbligatorio"); return; }
    setBusy(true);
    try {
      if (isNew) await api.post("/refactor/unita", f);
      else await api.put(`/refactor/unita/${data.id}`, f);
      toast.success(isNew ? "Unità creata" : "Unità aggiornata");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title={isNew ? "Nuova unità immobiliare" : "Modifica unità"} onClose={onClose} testid="unita-modal">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Interno"><Input data-testid="f-interno" value={f.interno} onChange={(e) => set("interno", e.target.value)} /></Field>
        <Field label="Palazzina"><Input data-testid="f-palazzina" value={f.palazzina} onChange={(e) => set("palazzina", e.target.value)} /></Field>
        <Field label="Scala"><Input data-testid="f-scala" value={f.scala} onChange={(e) => set("scala", e.target.value)} /></Field>
        <Field label="Piano"><Input data-testid="f-piano" value={f.piano} onChange={(e) => set("piano", e.target.value)} /></Field>
        <Field label="Tipo unità"><Input data-testid="f-tipo" value={f.tipo_unita} onChange={(e) => set("tipo_unita", e.target.value)} placeholder="Appartamento, Box, Cantina…" /></Field>
        <div className="col-span-2 border-t pt-3 mt-1">
          <div className="text-sm font-bold text-slate-700 mb-2">Dati catastali (chiave univoca)</div>
        </div>
        <Field label="Comune catastale"><Input data-testid="f-comune" value={f.comune_catastale} onChange={(e) => set("comune_catastale", e.target.value)} /></Field>
        <Field label="Sezione"><Input data-testid="f-sez" value={f.sezione_urbana} onChange={(e) => set("sezione_urbana", e.target.value)} /></Field>
        <Field label="Foglio"><Input data-testid="f-foglio" value={f.foglio} onChange={(e) => set("foglio", e.target.value)} /></Field>
        <Field label="Mappale/Particella"><Input data-testid="f-mappale" value={f.mappale_particella} onChange={(e) => set("mappale_particella", e.target.value)} /></Field>
        <Field label="Subalterno *"><Input data-testid="f-sub" value={f.sub} onChange={(e) => set("sub", e.target.value)} required /></Field>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <Button variant="outline" onClick={onClose}>Annulla</Button>
        <Button className="bg-brand hover:bg-brand-dark" disabled={busy} onClick={save} data-testid="save-unita">Salva</Button>
      </div>
    </ModalShell>
  );
}

/* ---------- SOGGETTO MODAL ---------- */
function SoggettoModal({ isNew, data, onClose, onSaved }) {
  const [f, setF] = useState({
    nome: data.nome || "", codice_fiscale: data.codice_fiscale || "",
    tipo: data.tipo || "Persona fisica",
    denominazione: data.denominazione || "",
    email: data.email || "", telefono: data.telefono || "", pec: data.pec || "",
    indirizzo: data.indirizzo || "", cap: data.cap || "",
    citta: data.citta || "", prov: data.prov || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.nome.trim()) { toast.error("Nome obbligatorio"); return; }
    setBusy(true);
    try {
      if (isNew) await api.post("/refactor/soggetti", f);
      else await api.put(`/refactor/soggetti/${data.id}`, f);
      toast.success(isNew ? "Soggetto creato" : "Soggetto aggiornato");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title={isNew ? "Nuovo soggetto" : "Modifica soggetto"} onClose={onClose} testid="soggetto-modal">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Nome / Ragione sociale *" full><Input data-testid="s-nome" value={f.nome} onChange={(e) => set("nome", e.target.value)} /></Field>
        <Field label="Codice Fiscale"><Input data-testid="s-cf" value={f.codice_fiscale} onChange={(e) => set("codice_fiscale", e.target.value.toUpperCase())} /></Field>
        <Field label="Tipo">
          <select data-testid="s-tipo" value={f.tipo} onChange={(e) => set("tipo", e.target.value)}
            className="h-11 w-full px-3 border-2 border-slate-300 rounded-lg bg-white">
            <option>Persona fisica</option>
            <option>Persona giuridica</option>
          </select>
        </Field>
        <Field label="Email"><Input data-testid="s-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} /></Field>
        <Field label="Telefono"><Input data-testid="s-tel" value={f.telefono} onChange={(e) => set("telefono", e.target.value)} /></Field>
        <Field label="PEC"><Input value={f.pec} onChange={(e) => set("pec", e.target.value)} /></Field>
        <div className="col-span-2"><Field label="Indirizzo residenza" full><Input value={f.indirizzo} onChange={(e) => set("indirizzo", e.target.value)} /></Field></div>
        <Field label="CAP"><Input value={f.cap} onChange={(e) => set("cap", e.target.value)} /></Field>
        <Field label="Città"><Input value={f.citta} onChange={(e) => set("citta", e.target.value)} /></Field>
        <Field label="Provincia"><Input value={f.prov} onChange={(e) => set("prov", e.target.value)} /></Field>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <Button variant="outline" onClick={onClose}>Annulla</Button>
        <Button className="bg-brand hover:bg-brand-dark" disabled={busy} onClick={save} data-testid="save-soggetto">Salva</Button>
      </div>
    </ModalShell>
  );
}

/* ---------- RELAZIONE MODAL (NEW) ---------- */
function RelazioneModal({ unita, soggetti, onClose, onSaved }) {
  const [mode, setMode] = useState("existing"); // existing | new
  const [soggettoId, setSoggettoId] = useState("");
  const [newSogg, setNewSogg] = useState({ nome: "", codice_fiscale: "", email: "", telefono: "" });
  const [f, setF] = useState({ ruolo: "Proprietario", quota: "", diritto_reale: "", tipo_casa: "", num_occupanti: "", percentuale_detrazione: "" });
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const setF2 = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const filteredSogg = useMemo(() => {
    const q = (query || "").toLowerCase().trim();
    if (!q) return soggetti.slice(0, 30);
    return soggetti.filter((s) => [s.nome, s.codice_fiscale, s.email].some((v) => (v || "").toLowerCase().includes(q))).slice(0, 50);
  }, [soggetti, query]);

  const save = async () => {
    setBusy(true);
    try {
      let sid = soggettoId;
      if (mode === "new") {
        if (!newSogg.nome.trim()) { toast.error("Nome soggetto obbligatorio"); setBusy(false); return; }
        const { data } = await api.post("/refactor/soggetti", newSogg);
        sid = data.id;
      }
      if (!sid) { toast.error("Seleziona un soggetto"); setBusy(false); return; }
      await api.post("/refactor/soggetti-unita", { unita_id: unita.id, soggetto_id: sid, ...f });
      toast.success("Soggetto collegato");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Collegamento non riuscito"); }
    finally { setBusy(false); }
  };

  return (
    <ModalShell title={`Collega soggetto a int. ${unita.interno || unita.sub}`} onClose={onClose} testid="rel-modal">
      <div className="flex gap-2 mb-4">
        <button onClick={() => setMode("existing")} data-testid="mode-existing"
          className={`h-10 px-3 rounded font-bold text-sm border-2 ${mode === "existing" ? "border-brand bg-[#FBEEF3] text-brand" : "border-slate-300 text-slate-600"}`}>
          Soggetto esistente
        </button>
        <button onClick={() => setMode("new")} data-testid="mode-new"
          className={`h-10 px-3 rounded font-bold text-sm border-2 ${mode === "new" ? "border-brand bg-[#FBEEF3] text-brand" : "border-slate-300 text-slate-600"}`}>
          Nuovo soggetto
        </button>
      </div>

      {mode === "existing" && (
        <div className="mb-4">
          <Input placeholder="Cerca per nome, CF, email…" value={query} onChange={(e) => setQuery(e.target.value)} className="mb-2" data-testid="rel-search" />
          <div className="max-h-52 overflow-y-auto border rounded divide-y">
            {filteredSogg.map((s) => (
              <button key={s.id} onClick={() => setSoggettoId(s.id)} data-testid={`rel-sel-${s.id}`}
                className={`w-full text-left p-2 hover:bg-slate-50 ${soggettoId === s.id ? "bg-[#FBEEF3]" : ""}`}>
                <div className="font-semibold text-sm">{highlightMatch(s.nome, query)}</div>
                <div className="text-xs text-slate-500">{highlightMatch(s.codice_fiscale || "senza CF", query)} {s.email && <>· {highlightMatch(s.email, query)}</>}</div>
              </button>
            ))}
            {filteredSogg.length === 0 && <div className="p-3 text-sm text-slate-400 text-center">Nessuno.</div>}
          </div>
        </div>
      )}

      {mode === "new" && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Field label="Nome *" full><Input data-testid="ns-nome" value={newSogg.nome} onChange={(e) => setNewSogg({ ...newSogg, nome: e.target.value })} /></Field>
          <Field label="CF"><Input data-testid="ns-cf" value={newSogg.codice_fiscale} onChange={(e) => setNewSogg({ ...newSogg, codice_fiscale: e.target.value.toUpperCase() })} /></Field>
          <Field label="Email"><Input value={newSogg.email} onChange={(e) => setNewSogg({ ...newSogg, email: e.target.value })} /></Field>
          <Field label="Telefono" full><Input value={newSogg.telefono} onChange={(e) => setNewSogg({ ...newSogg, telefono: e.target.value })} /></Field>
        </div>
      )}

      <div className="border-t pt-3">
        <div className="text-sm font-bold text-slate-700 mb-2">Dettagli relazione</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Ruolo *">
            <select data-testid="rel-ruolo" value={f.ruolo} onChange={(e) => setF2("ruolo", e.target.value)}
              className="h-11 w-full px-3 border-2 border-slate-300 rounded-lg bg-white">
              {RUOLI_ALL.map((r) => <option key={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Quota (%)"><Input data-testid="rel-quota" value={f.quota} onChange={(e) => setF2("quota", e.target.value)} placeholder="100" /></Field>
          <Field label="Diritto reale"><Input value={f.diritto_reale} onChange={(e) => setF2("diritto_reale", e.target.value)} /></Field>
          <Field label="Tipo casa"><Input value={f.tipo_casa} onChange={(e) => setF2("tipo_casa", e.target.value)} placeholder="Prima casa, Seconda…" /></Field>
          <Field label="N. occupanti"><Input value={f.num_occupanti} onChange={(e) => setF2("num_occupanti", e.target.value)} /></Field>
          <Field label="% Detrazione"><Input value={f.percentuale_detrazione} onChange={(e) => setF2("percentuale_detrazione", e.target.value)} /></Field>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <Button variant="outline" onClick={onClose}>Annulla</Button>
        <Button className="bg-brand hover:bg-brand-dark" disabled={busy} onClick={save} data-testid="save-rel">Collega</Button>
      </div>
    </ModalShell>
  );
}

/* ---------- SUBENTRO / VENDITA MODAL (Iter121s · Fase 2) ---------- */
function SubentroModal({ unita, relazioneUscente, soggetti, onClose, onSaved }) {
  const soggAttivi = (unita.soggetti_collegati || []).filter((s) => RUOLI_PROPR.includes(s.ruolo) || RUOLI_ALTRI_DIRITTI.includes(s.ruolo));
  const [uscenteId, setUscenteId] = useState(relazioneUscente ? relazioneUscente.relazione_id : "");
  const [mode, setMode] = useState("existing"); // existing | new
  const [soggettoId, setSoggettoId] = useState("");
  const [query, setQuery] = useState("");
  const [newSogg, setNewSogg] = useState({ nome: "", codice_fiscale: "", email: "", telefono: "", tipo: "fisico" });
  const [f, setF] = useState({
    ruolo: relazioneUscente ? (relazioneUscente.ruolo || "Proprietario") : "Proprietario",
    quota: relazioneUscente ? (relazioneUscente.quota || "") : "100",
    data_evento: new Date().toISOString().slice(0, 10),
    motivo_uscita: "Vendita",
    come_referente: true,
  });
  const [busy, setBusy] = useState(false);
  const setF2 = (k, v) => setF((s) => ({ ...s, [k]: v }));

  // Escludi i soggetti già collegati (evita di far subentrare chi è già presente)
  const idsCollegati = new Set((unita.soggetti_collegati || []).map((s) => s.soggetto_id).filter(Boolean));
  const filteredSogg = useMemo(() => {
    const q = (query || "").toLowerCase().trim();
    const base = (soggetti || []).filter((s) => !idsCollegati.has(s.id));
    if (!q) return base.slice(0, 30);
    return base.filter((s) => [s.nome, s.codice_fiscale, s.email].some((v) => (v || "").toLowerCase().includes(q))).slice(0, 50);
  }, [soggetti, query, idsCollegati]);

  const uscenteObj = uscenteId ? soggAttivi.find((s) => s.relazione_id === uscenteId) : null;

  const save = async () => {
    setBusy(true);
    try {
      // Validazioni minime lato client
      if (mode === "existing" && !soggettoId) {
        toast.error("Seleziona il soggetto subentrante");
        setBusy(false); return;
      }
      if (mode === "new" && !newSogg.nome.trim()) {
        toast.error("Nome del nuovo soggetto obbligatorio");
        setBusy(false); return;
      }
      const payload = {
        relazione_uscente_id: uscenteId || null,
        motivo_uscita: f.motivo_uscita || "Vendita",
        data_evento: f.data_evento || undefined,
        ruolo: f.ruolo,
        quota: f.quota,
        come_referente: !!f.come_referente,
        subentrante: mode === "existing"
          ? { soggetto_id: soggettoId }
          : {
              nome: newSogg.nome.trim(),
              codice_fiscale: (newSogg.codice_fiscale || "").toUpperCase().trim(),
              email: (newSogg.email || "").toLowerCase().trim(),
              telefono: (newSogg.telefono || "").trim(),
              tipo: newSogg.tipo || "fisico",
            },
      };
      const { data } = await api.post(`/refactor/unita/${unita.id}/subentro`, payload);
      const created = (data?.steps || []).some((s) => s.azione === "soggetto_creato");
      const closed = (data?.steps || []).some((s) => s.azione === "chiusura_relazione");
      toast.success(`Subentro registrato${closed ? " · uscente archiviato" : ""}${created ? " · nuovo soggetto creato" : ""}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Subentro non riuscito");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title={`Subentro / Vendita · int. ${unita.interno || unita.sub}`} onClose={onClose} testid="subentro-modal">
      {/* STEP 1 · Uscente (opzionale) */}
      <div className="mb-4 border-2 border-amber-200 bg-amber-50 rounded-lg p-3">
        <div className="text-xs font-bold text-amber-800 uppercase tracking-wide mb-2 flex items-center gap-1">
          <ArrowRightLeft className="w-3.5 h-3.5" /> 1 · Titolare uscente <span className="text-amber-600 font-normal normal-case">(opzionale — se assente sarà un subentro puro)</span>
        </div>
        {soggAttivi.length === 0 ? (
          <div className="text-sm text-amber-700 italic">Nessun titolare attivo su questa unità: procedi con la sola apertura del subentrante.</div>
        ) : (
          <select data-testid="subentro-uscente" value={uscenteId} onChange={(e) => setUscenteId(e.target.value)}
            className="h-10 w-full px-3 border-2 border-amber-300 rounded-lg bg-white text-sm">
            <option value="">— Nessuno (subentro puro) —</option>
            {soggAttivi.map((s) => (
              <option key={s.relazione_id} value={s.relazione_id}>
                {s.nome} · {s.ruolo}{s.quota ? ` · ${s.quota}%` : ""}{s.e_referente_account ? " · referente" : ""}
              </option>
            ))}
          </select>
        )}
        {uscenteObj && (
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Field label="Motivo uscita">
              <select value={f.motivo_uscita} onChange={(e) => setF2("motivo_uscita", e.target.value)}
                data-testid="subentro-motivo"
                className="h-10 w-full px-3 border-2 border-slate-300 rounded-lg bg-white text-sm">
                <option>Vendita</option>
                <option>Successione</option>
                <option>Donazione</option>
                <option>Cessione quote</option>
                <option>Altro</option>
              </select>
            </Field>
            <Field label="Data evento">
              <Input type="date" value={f.data_evento} onChange={(e) => setF2("data_evento", e.target.value)} data-testid="subentro-data" />
            </Field>
          </div>
        )}
      </div>

      {/* STEP 2 · Subentrante */}
      <div className="mb-4">
        <div className="text-xs font-bold text-emerald-800 uppercase tracking-wide mb-2 flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> 2 · Soggetto subentrante
        </div>
        <div className="flex gap-2 mb-3">
          <button onClick={() => setMode("existing")} data-testid="subentro-mode-existing"
            className={`h-10 px-3 rounded font-bold text-sm border-2 ${mode === "existing" ? "border-brand bg-[#FBEEF3] text-brand" : "border-slate-300 text-slate-600"}`}>
            Soggetto esistente
          </button>
          <button onClick={() => setMode("new")} data-testid="subentro-mode-new"
            className={`h-10 px-3 rounded font-bold text-sm border-2 ${mode === "new" ? "border-brand bg-[#FBEEF3] text-brand" : "border-slate-300 text-slate-600"}`}>
            Nuovo soggetto
          </button>
        </div>

        {mode === "existing" && (
          <div>
            <Input placeholder="Cerca per nome, CF, email…" value={query} onChange={(e) => setQuery(e.target.value)} className="mb-2" data-testid="subentro-search" />
            <div className="max-h-52 overflow-y-auto border rounded divide-y">
              {filteredSogg.map((s) => (
                <button key={s.id} onClick={() => setSoggettoId(s.id)} data-testid={`subentro-sel-${s.id}`}
                  className={`w-full text-left p-2 hover:bg-slate-50 ${soggettoId === s.id ? "bg-[#FBEEF3]" : ""}`}>
                  <div className="font-semibold text-sm">{highlightMatch(s.nome, query)}</div>
                  <div className="text-xs text-slate-500">{highlightMatch(s.codice_fiscale || "senza CF", query)}{s.email ? <>{" · "}{highlightMatch(s.email, query)}</> : ""}</div>
                </button>
              ))}
              {filteredSogg.length === 0 && <div className="p-3 text-sm text-slate-400 text-center">Nessuno.</div>}
            </div>
          </div>
        )}

        {mode === "new" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nome *" full><Input data-testid="subentro-ns-nome" value={newSogg.nome} onChange={(e) => setNewSogg({ ...newSogg, nome: e.target.value })} /></Field>
            <Field label="Codice fiscale"><Input data-testid="subentro-ns-cf" value={newSogg.codice_fiscale} onChange={(e) => setNewSogg({ ...newSogg, codice_fiscale: e.target.value.toUpperCase() })} /></Field>
            <Field label="Tipo">
              <select value={newSogg.tipo} onChange={(e) => setNewSogg({ ...newSogg, tipo: e.target.value })}
                className="h-11 w-full px-3 border-2 border-slate-300 rounded-lg bg-white text-sm">
                <option value="fisico">Persona fisica</option>
                <option value="giuridico">Persona giuridica</option>
              </select>
            </Field>
            <Field label="Email"><Input value={newSogg.email} onChange={(e) => setNewSogg({ ...newSogg, email: e.target.value })} /></Field>
            <Field label="Telefono" full><Input value={newSogg.telefono} onChange={(e) => setNewSogg({ ...newSogg, telefono: e.target.value })} /></Field>
          </div>
        )}
      </div>

      {/* STEP 3 · Nuova relazione */}
      <div className="border-t pt-3">
        <div className="text-xs font-bold text-sky-800 uppercase tracking-wide mb-2 flex items-center gap-1">
          <Link2 className="w-3.5 h-3.5" /> 3 · Dettagli nuova relazione
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Ruolo *">
            <select data-testid="subentro-ruolo" value={f.ruolo} onChange={(e) => setF2("ruolo", e.target.value)}
              className="h-11 w-full px-3 border-2 border-slate-300 rounded-lg bg-white">
              {RUOLI_ALL.map((r) => <option key={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Quota (%)"><Input data-testid="subentro-quota" value={f.quota} onChange={(e) => setF2("quota", e.target.value)} placeholder="100" /></Field>
          {!uscenteObj && (
            <Field label="Data decorrenza">
              <Input type="date" value={f.data_evento} onChange={(e) => setF2("data_evento", e.target.value)} data-testid="subentro-data-dal" />
            </Field>
          )}
          <Field label="Referente account" full>
            <label className="flex items-center gap-2 text-sm mt-1">
              <input type="checkbox" checked={!!f.come_referente} onChange={(e) => setF2("come_referente", e.target.checked)} data-testid="subentro-referente" className="w-4 h-4 accent-brand" />
              Imposta il subentrante come referente account per l&apos;unità (sostituisce l&apos;attuale)
            </label>
          </Field>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <Button variant="outline" onClick={onClose}>Annulla</Button>
        <Button className="bg-brand hover:bg-brand-dark" disabled={busy} onClick={save} data-testid="subentro-save">
          {uscenteObj ? "Registra subentro" : "Apri nuovo titolare"}
        </Button>
      </div>
    </ModalShell>
  );
}


/* ---------- RELAZIONE MODAL (EDIT) — Iter121c Fase 3: 2 tab con Dati RAC ---------- */
function RelazioneEditModal({ unita, relazione, onClose, onSaved }) {
  const [tab, setTab] = useState("ruolo"); // "ruolo" | "rac"
  const [f, setF] = useState({
    ruolo: relazione.ruolo || "Proprietario",
    quota: relazione.quota || "",
    diritto_reale: relazione.diritto_reale || "",
    tipo_casa: relazione.tipo_casa || "",
    num_occupanti: relazione.num_occupanti || "",
    percentuale_detrazione: relazione.percentuale_detrazione || "",
    tipo_possesso: relazione.tipo_possesso || "",
    inizio_locazione: relazione.inizio_locazione || "",
    fine_locazione: relazione.fine_locazione || "",
  });
  const [titolari, setTitolari] = useState(relazione.titolari_secondari || []);
  const [referenteToggle, setReferenteToggle] = useState(!!relazione.e_referente_account);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const canBeReferente = ["Proprietario", "Comproprietario"].includes(f.ruolo);
  const isConduttore = f.ruolo === "Inquilino/Conduttore";

  const addTitolare = () => setTitolari((a) => [...a, { nome: "", codice_fiscale: "", email: "", telefono: "", diritto_reale: "", quota_diritto: "", tipo_possesso: "", percentuale_detrazione: "", num_occupanti: "" }]);
  const removeTitolare = (i) => setTitolari((a) => a.filter((_, j) => j !== i));
  const setTit = (i, key, v) => setTitolari((a) => a.map((t, j) => j === i ? {
    ...t, [key]: v,
    ...(key === "tipo_possesso" ? { percentuale_detrazione: (POSSESSO_OPZIONI.find((o) => o.value === v)?.detr) || t.percentuale_detrazione } : {})
  } : t));

  // Somma quote per validazione live (solo per proprietari)
  const totaleQuote = (Number(f.quota) || 0) + titolari.reduce((s, t) => s + (Number(t.quota_diritto) || 0), 0);
  const quoteOk = Math.abs(totaleQuote - 100) < 0.01;
  const showQuoteBadge = !isConduttore && (titolari.length > 0 || (Number(f.quota) || 0) > 0);

  // Completezza RAC (semplificata): per proprietari servono quota + diritto + tipo casa + detrazione + occupanti
  const racCompleta = isConduttore
    ? (f.inizio_locazione && f.fine_locazione && f.num_occupanti)
    : (f.quota && f.diritto_reale && f.tipo_casa && f.percentuale_detrazione && f.num_occupanti);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...f, titolari_secondari: titolari };
      const r = await api.put(`/refactor/soggetti-unita/${relazione.relazione_id}`, payload);
      const wasReferente = !!relazione.e_referente_account;
      if (canBeReferente && referenteToggle && !wasReferente) {
        await api.post(`/refactor/soggetti-unita/${relazione.relazione_id}/set-referente`);
      }
      const promossi = r.data?._promoted_titolari || [];
      if (promossi.length > 0) {
        toast.success(`Relazione aggiornata · ${promossi.length} comproprietario/i aggiunto/i all'unità (${promossi.join(", ")})`);
      } else {
        toast.success("Relazione aggiornata");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <ModalShell title={`Modifica relazione — ${relazione.nome}`} onClose={onClose} testid="rel-edit-modal">
      {/* Tab switcher */}
      <div className="flex gap-1 border-b border-slate-200 mb-4">
        <button
          data-testid="tab-ruolo"
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === "ruolo" ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-800"}`}
          onClick={() => setTab("ruolo")}>
          Ruolo & referente
        </button>
        <button
          data-testid="tab-rac"
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${tab === "rac" ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-800"}`}
          onClick={() => setTab("rac")}>
          Dati RAC
          {racCompleta
            ? <span title="Dati RAC completi" className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
            : <span title="Dati RAC incompleti" className="w-2 h-2 rounded-full bg-amber-500 inline-block" />}
        </button>
      </div>

      {tab === "ruolo" && (
        <div data-testid="rel-tab-ruolo">
          <Field label="Ruolo">
            <select data-testid="rel-ruolo" value={f.ruolo} onChange={(e) => set("ruolo", e.target.value)}
              className="h-11 w-full px-3 border-2 border-slate-300 rounded-lg bg-white">
              {RUOLI_ALL.map((r) => <option key={r}>{r}</option>)}
            </select>
          </Field>
          <div className={`mt-4 rounded-lg border-2 px-3 py-2 flex items-center gap-2 ${canBeReferente ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50 opacity-60"}`}>
            <input type="checkbox" data-testid="rel-referente-toggle" checked={referenteToggle}
              disabled={!canBeReferente}
              onChange={(e) => setReferenteToggle(e.target.checked)} className="w-4 h-4" />
            <label className="text-sm">
              <b>Referente account</b> per questa unità
              {!canBeReferente && <span className="ml-2 text-xs text-slate-500">(solo Proprietario/Comproprietario)</span>}
            </label>
          </div>
          {relazione.rac_ultimo_aggiornamento_at && (
            <div className="mt-3 text-xs text-slate-500">
              Ultimo aggiornamento RAC: {new Date(relazione.rac_ultimo_aggiornamento_at).toLocaleString("it-IT")}
            </div>
          )}
        </div>
      )}

      {tab === "rac" && (
        <div data-testid="rel-tab-rac" className="space-y-4">
          {!isConduttore ? (
            <>
              {showQuoteBadge && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Somma quota principale + titolari secondari:</span>
                  <span data-testid="quote-total-badge" className={`text-xs font-bold px-2 py-1 rounded border ${quoteOk ? "bg-green-100 text-green-800 border-green-300" : (totaleQuote > 100 ? "bg-red-100 text-red-800 border-red-300" : "bg-amber-100 text-amber-800 border-amber-300")}`}>
                    {totaleQuote}% {quoteOk ? "✓" : (totaleQuote > 100 ? "(supera 100%)" : "(non chiude 100%)")}
                  </span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Quota di proprietà (%)"><Input data-testid="rel-quota" type="number" value={f.quota} onChange={(e) => set("quota", e.target.value)} /></Field>
                <Field label="Diritto reale">
                  <select data-testid="rel-diritto" value={f.diritto_reale} onChange={(e) => set("diritto_reale", e.target.value)}
                    className="h-11 w-full px-3 border-2 border-slate-300 rounded-lg bg-white">
                    <option value="">— Seleziona —</option>
                    {DIRITTI_REALI.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Tipologia immobile">
                  <select data-testid="rel-tipo-casa" value={f.tipo_casa} onChange={(e) => set("tipo_casa", e.target.value)}
                    className="h-11 w-full px-3 border-2 border-slate-300 rounded-lg bg-white">
                    <option value="">— Seleziona —</option>
                    {TIPI_CASA.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="% Detrazione fiscale"><Input data-testid="rel-detr" type="number" value={f.percentuale_detrazione} onChange={(e) => set("percentuale_detrazione", e.target.value)} /></Field>
                <Field label="N. occupanti"><Input data-testid="rel-occ" type="number" value={f.num_occupanti} onChange={(e) => set("num_occupanti", e.target.value)} /></Field>
                <Field label="Tipologia possesso">
                  <select data-testid="rel-poss" value={f.tipo_possesso} onChange={(e) => set("tipo_possesso", e.target.value)}
                    className="h-11 w-full px-3 border-2 border-slate-300 rounded-lg bg-white">
                    <option value="">— Seleziona —</option>
                    {POSSESSO_OPZIONI.map((o) => <option key={o.value} value={o.value}>{o.value}</option>)}
                  </select>
                </Field>
              </div>

              {/* Titolari secondari (comproprietari inline) */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-sm font-semibold text-slate-700">Altri titolari di diritti sull&apos;unità</Label>
                  <Button variant="outline" size="sm" data-testid="add-titolare" onClick={addTitolare} className="h-8 text-xs">
                    <Plus className="w-3 h-3 mr-1" /> Aggiungi
                  </Button>
                </div>
                {titolari.length === 0 && <p className="text-xs text-slate-400 italic">Nessun comproprietario. Usa &quot;Aggiungi&quot; per collegare comproprietari/titolari di diritti — al salvataggio compariranno come soggetti collegati dell&apos;unità.</p>}
                {titolari.map((t, i) => (
                  <div key={i} data-testid={`titolare-${i}`} className="mb-2 border-2 border-slate-200 rounded-lg p-3 bg-slate-50/50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-slate-600">
                        {t.nome || `Titolare ${i + 1}`}{t.diritto_reale ? ` · ${t.diritto_reale}` : ""}{t.quota_diritto ? ` · ${t.quota_diritto}%` : ""}
                      </span>
                      <button type="button" data-testid={`titolare-remove-${i}`} onClick={() => removeTitolare(i)} className="text-red-500 hover:text-red-700">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="col-span-2">
                        <Label className="text-xs">Cognome e nome</Label>
                        <Input data-testid={`tit-nome-${i}`} className="h-9 text-sm" value={t.nome} onChange={(e) => setTit(i, "nome", e.target.value)} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Codice fiscale</Label>
                        <Input data-testid={`tit-cf-${i}`} className="h-9 text-sm uppercase" value={t.codice_fiscale} onChange={(e) => setTit(i, "codice_fiscale", (e.target.value || "").toUpperCase())} />
                      </div>
                      {/* Iter123k162 · Email e telefono per contattare direttamente il comproprietario */}
                      <div>
                        <Label className="text-xs">Email</Label>
                        <Input data-testid={`tit-email-${i}`} type="email" className="h-9 text-sm" value={t.email || ""} onChange={(e) => setTit(i, "email", e.target.value)} placeholder="es. nome@dominio.it" />
                      </div>
                      <div>
                        <Label className="text-xs">Telefono</Label>
                        <Input data-testid={`tit-tel-${i}`} className="h-9 text-sm" value={t.telefono || ""} onChange={(e) => setTit(i, "telefono", e.target.value)} placeholder="es. +39 333 1234567" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Tipologia diritto</Label>
                        <select data-testid={`tit-diritto-${i}`} value={t.diritto_reale} onChange={(e) => setTit(i, "diritto_reale", e.target.value)}
                          className="h-9 w-full px-2 border-2 border-slate-300 rounded-lg bg-white text-sm">
                          <option value="">— Seleziona —</option>
                          {DIRITTI_REALI.map((o) => <option key={o}>{o}</option>)}
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">% diritto</Label>
                        <Input data-testid={`tit-quota-${i}`} type="number" className="h-9 text-sm" value={t.quota_diritto} onChange={(e) => setTit(i, "quota_diritto", e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-xs">N. occupanti</Label>
                        <Input data-testid={`tit-occ-${i}`} type="number" className="h-9 text-sm" value={t.num_occupanti} onChange={(e) => setTit(i, "num_occupanti", e.target.value)} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Tipologia possesso</Label>
                        <select data-testid={`tit-poss-${i}`} value={t.tipo_possesso} onChange={(e) => setTit(i, "tipo_possesso", e.target.value)}
                          className="h-9 w-full px-2 border-2 border-slate-300 rounded-lg bg-white text-sm">
                          <option value="">— Seleziona —</option>
                          {POSSESSO_OPZIONI.map((o) => <option key={o.value} value={o.value}>{o.value}</option>)}
                        </select>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">% detrazione</Label>
                        <Input data-testid={`tit-detr-${i}`} type="number" className="h-9 text-sm" value={t.percentuale_detrazione} onChange={(e) => setTit(i, "percentuale_detrazione", e.target.value)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Inizio locazione"><Input data-testid="rel-inizio-loc" type="date" value={f.inizio_locazione} onChange={(e) => set("inizio_locazione", e.target.value)} /></Field>
              <Field label="Fine locazione"><Input data-testid="rel-fine-loc" type="date" value={f.fine_locazione} onChange={(e) => set("fine_locazione", e.target.value)} /></Field>
              <Field label="N. occupanti"><Input data-testid="rel-occ" type="number" value={f.num_occupanti} onChange={(e) => set("num_occupanti", e.target.value)} /></Field>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-5">
        <Button variant="outline" onClick={onClose}>Annulla</Button>
        <Button className="bg-brand hover:bg-brand-dark" disabled={busy} onClick={save} data-testid="save-rel-edit">Salva</Button>
      </div>
    </ModalShell>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-sm font-semibold text-slate-700 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

/* ============================ FASE 4 · MIGRATION PANEL ============================
   Visualizzato quando un condominio NON migrato è selezionato. Consente all'admin di:
   - Vedere anteprima conflitti (CF/catasto duplicati, record incompleti)
   - Eseguire dry-run migrazione (nessuna scrittura)
   - Eseguire migrazione reale
   - Validare i conteggi post-migrazione
   - Rollback se qualcosa va storto
*/
function MigrationPanel({ cond, condNome, onMigrated }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dryReport, setDryReport] = useState(null);
  const [execReport, setExecReport] = useState(null);
  const [validate, setValidate] = useState(null);

  useEffect(() => {
    setPreview(null); setDryReport(null); setExecReport(null); setValidate(null);
    api.get(`/refactor/migrate/preview/${cond}`).then((r) => setPreview(r.data)).catch(() => {});
  }, [cond]);

  const runDry = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/refactor/migrate/execute/${cond}?dry_run=true`); setDryReport(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const runExec = async () => {
    if (!window.confirm(`⚠️ Eseguire la MIGRAZIONE reale di "${condNome}"?\n\nVerranno creati record in unita_immobiliari, soggetti, soggetti_unita.\nIl legacy proprieta NON viene toccato.\nPuoi annullare col rollback dopo.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/refactor/migrate/execute/${cond}?dry_run=false`);
      setExecReport(data);
      toast.success(`Migrato ${condNome}: ${data.soggetti_creati} soggetti, ${data.unita_create} unità, ${data.relazioni_create} relazioni`);
      onMigrated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const runValidate = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/refactor/migrate/validate/${cond}`); setValidate(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const runRollback = async () => {
    if (!execReport?.batch_id) return;
    if (!window.confirm(`Rollback della migrazione (batch ${execReport.batch_id.substring(0, 8)}...)?\nElimina i record creati sul nuovo modello (il legacy resta intatto).`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/refactor/migrate/rollback/${execReport.batch_id}`);
      toast.success(`Rollback completato: ${data.soggetti} soggetti, ${data.unita_immobiliari} unità, ${data.soggetti_unita} relazioni eliminate`);
      setExecReport(null); setValidate(null);
      onMigrated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!preview) return <Card className="p-8 text-center text-slate-400">Caricamento anteprima…</Card>;

  const hasConflicts = (Object.keys(preview.conflitti.cf_duplicati).length + Object.keys(preview.conflitti.catasto_duplicati).length +
                       preview.conflitti.senza_cf_e_senza_nome + preview.conflitti.senza_sub_e_senza_interno) > 0;

  return (
    <Card className="p-6 border-2 border-fuchsia-300 bg-fuchsia-50" data-testid="migration-panel">
      <div className="mb-4">
        <div className="font-bold text-lg text-fuchsia-900 mb-2">Strumenti Fase 4 — Migrazione al nuovo modello</div>
        <div className="text-sm text-fuchsia-800">Il condominio <b>{condNome}</b> non è ancora migrato. Puoi convertirlo dal legacy senza toccare i dati esistenti.</div>
      </div>

      {/* Anteprima */}
      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-white rounded-lg p-3 border">
          <div className="text-xs text-slate-500 uppercase font-bold">Legacy attuale</div>
          <div className="text-2xl font-bold text-brand">{preview.legacy.proprieta_totali}</div>
          <div className="text-xs text-slate-600">record proprieta<br/>≈ {preview.legacy.soggetti_distinti_stimati} soggetti distinti</div>
        </div>
        <div className="bg-white rounded-lg p-3 border">
          <div className="text-xs text-slate-500 uppercase font-bold">Nuovo modello</div>
          <div className="text-2xl font-bold text-fuchsia-700">{preview.nuovo_modello.unita_immobiliari}</div>
          <div className="text-xs text-slate-600">unità · {preview.nuovo_modello.soggetti_collegati} soggetti</div>
        </div>
        <div className="bg-white rounded-lg p-3 border">
          <div className="text-xs text-slate-500 uppercase font-bold">Conflitti</div>
          <div className={`text-2xl font-bold ${hasConflicts ? "text-red-600" : "text-emerald-600"}`}>
            {hasConflicts ? "⚠" : "✓"}
          </div>
          <div className="text-xs text-slate-600">
            {preview.conflitti.senza_cf_e_senza_nome > 0 && <div>{preview.conflitti.senza_cf_e_senza_nome} senza dati</div>}
            {Object.keys(preview.conflitti.cf_duplicati).length > 0 && <div>{Object.keys(preview.conflitti.cf_duplicati).length} CF duplicati</div>}
            {Object.keys(preview.conflitti.catasto_duplicati).length > 0 && <div>{Object.keys(preview.conflitti.catasto_duplicati).length} catasti duplicati (comproprietari)</div>}
            {!hasConflicts && "Nessuno"}
          </div>
        </div>
      </div>

      {hasConflicts && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded text-xs text-amber-900">
          <b>Nota sui conflitti:</b> i CF duplicati e i catasti duplicati NON impediscono la migrazione — sono normali per comproprietari e verranno aggregati automaticamente. Solo i record &quot;senza dati&quot; saranno scartati.
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button data-testid="migrate-dry-btn" disabled={busy} onClick={runDry} variant="outline" className="border-2 border-fuchsia-500 text-fuchsia-700">
          1. Dry-run (anteprima gruppi)
        </Button>
        <Button data-testid="migrate-exec-btn" disabled={busy || !dryReport} onClick={runExec} className="bg-fuchsia-700 hover:bg-fuchsia-800 text-white">
          2. Esegui migrazione
        </Button>
        <Button data-testid="migrate-validate-btn" disabled={busy || !execReport} onClick={runValidate} variant="outline" className="border-2 border-emerald-500 text-emerald-700">
          3. Valida conteggi
        </Button>
        <Button data-testid="migrate-rollback-btn" disabled={busy || !execReport} onClick={runRollback} variant="outline" className="border-2 border-red-500 text-red-700 ml-auto">
          Rollback
        </Button>
      </div>

      {dryReport && (
        <div className="mt-4 p-3 bg-white rounded border text-sm">
          <div className="font-bold mb-1">Dry-run OK</div>
          <div>Gruppi soggetti costruiti: <b>{dryReport.gruppi_costruiti}</b> · righe totali: <b>{dryReport.righe_totali}</b></div>
        </div>
      )}
      {execReport && (
        <div className="mt-4 p-3 bg-emerald-50 border border-emerald-300 rounded text-sm">
          <div className="font-bold text-emerald-900">✅ Migrazione completata (batch {execReport.batch_id.substring(0, 8)}…)</div>
          <div>Creati: {execReport.soggetti_creati} soggetti · {execReport.unita_create} unità · {execReport.relazioni_create} relazioni</div>
          <div>Aggiornati: {execReport.soggetti_aggiornati} soggetti · {execReport.unita_aggiornate} unità · {execReport.relazioni_aggiornate} relazioni</div>
        </div>
      )}
      {validate && (
        <div className="mt-4 p-3 bg-slate-50 rounded border text-sm">
          <div className="font-bold mb-1">Validazione conteggi</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>Legacy: {validate.legacy.soggetti_distinti_stimati} soggetti · {validate.legacy.unita_distinte_stimate} unità · {validate.legacy.user_id_totali} user_id</div>
            <div>Nuovo: {validate.nuovo.soggetti_distinti} soggetti · {validate.nuovo.unita_immobiliari} unità · {validate.nuovo.user_id_collegati} user_id</div>
          </div>
          {validate.warning.users_non_collegati.length > 0 && (
            <div className="mt-2 text-amber-800 text-xs">
              ⚠ <b>{validate.warning.users_non_collegati.length} user_id</b> non trovano corrispondenza nel nuovo modello (probabilmente account creati senza `residente_id` corretto). Verifica manualmente.
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
